// SafeTag app entry. Admin pages: Sites, People & Badges, Gloves, Program Settings, Lists, Tasks, Reference Library, Email Log, Quiz Bank and Training Portal.
// Sign-in is the temporary email stand-in (2026-10-04) until tag and PIN lands.
// A quiz link (?quiz=2) opens the trainee quiz page instead. No sign-in there.
// An asset label (?aid=01-ER2-MCC-1A) opens the guest page. No sign-in there either.
// A QEW bag tag (?tag=01-QEW14) opens the tag page: the claim for an unclaimed tag.
// An admin tag falls through to the admin sign-in.
// Portal buttons call server code. The server checks every rule; the page shows the answer.

import { driveSettingsScreen, onDriveSettingsClick, takeConnectResult } from "./drive-settings-page";
import { onPrinterSetupChange, onPrinterSetupClick, printerSetupScreen } from "./printer-setup";
import { openScanAgain } from "./scan-again-view";
import { cameFromAdmin, mountAdminWayBack } from "./admin-way-back";
import "./styles.css";
import type { SignInClaims } from "./badge";
import {
  ServerError,
  badgeOf,
  callServer,
  failedInSession,
  loadSessions,
  loadGlovesData,
  loadListsData,
  loadPeopleData,
  loadTasksData,
  loadReferencesData,
  loadEmailData,
  loadWorkRequests,
  type WorkRequestData,
  loadNearMisses,
  loadContractorVisits,
  type NearMissData,
  type VisitData,
  referenceFileUrl,
  uploadReferenceFile,
  loadSettingsData,
  withTimeLimit,
  loadSiteAssets,
  loadAssetPage,
  loadSitesData,
  loadTraining,
  signIn,
  signOutNow,
  startFirebase,
  useSlot,
  watchSignIn,
  type SiteSessions,
  type GlovesData,
  type ListsData,
  type PeopleData,
  type SettingsData,
  type SitesData,
  type TasksData,
  type ReferencesData,
  type EmailData,
  type TrainingData, loadSiteProgram } from "./firebase";
import {
  closeByHandForm,
  editDateForm,
  classResultsScreen,
  endSessionSheet,
  expiryPreview,
  flashNote,
  pastSessionsBox,
  reopenSheet,
  sessionBox,
  type CloseByHandEntry,
} from "./portal-screens";
import { passMarkSheet, passMarkStats, questionForm, switchSheet } from "./quiz-bank-screens";
import { mountTagPage } from "./claim-page";
import { holdForSignal, isOnline, needsSignalScreen, opensOffline, registerServiceWorker } from "./offline";
import { dropAfSites, renderAfLabels } from "./af-labels-page";
import { dropSurveySites, renderSurvey } from "./survey-page";
import { nextSiteNumber, oldestSavedAt, siteBackOf } from "./site-stage";
import { assetView, type AssetPageData } from "./asset-page";
import { assetPageScreen } from "./asset-page-screens";
import { deviceCard, hubContacts, hubSteps, lastStepKey, peopleHere, readJsonMap, recordsHere, stageReady, stepOfRoute, stripKey, stripOf, whenLine, type HubInput, type LastStep, type ProgramData } from "./site-hub";
import { siteZone, zoneInfo, zoneFromState } from "./site-time";
import { siteHub, zoneSheet } from "./site-hub-screens";
import { esc as escapeHtml } from "./training-screens";
import type { Site } from "./sites";
import { loadSaved as loadFieldLabels, storageKey as flStorageKey } from "./field-labels-page";
import { deviceList } from "./outbox";
import { adminReturnPath, clearAdminReturn, mountFieldAccess, openQewVisit, takeAdminRequest } from "./field-access-page";
import { pageFromHash } from "./nav-history";
import { steps } from "./step-back";
import { isQewSignIn } from "./field-access";
import { mountGuestPage } from "./guest-page";
import { mountNewTrainee } from "./new-trainee-page";
import { isAdminBadgeId, readQrLink } from "./qr-link";
import { mountQuizPage, quizParamsFrom } from "./quiz-page";
import { mountDecidePage } from "./eewp-decide-page";
import { decisionParamsFrom } from "./eewp";
import type { Authority } from "./badge";
import { personGloves, rosterForData, type RosterRow } from "./gloves-roster";
import { homeContinue, homeGroups, homeSites as homeSiteRows, homeSubLine, sitesHeadLine, demoLabelId, guestUrl, type DemoLabelKey } from "./admin-home";
import { addSiteLines, addSiteNumberNote, addSiteSheet, addSiteZoneHint, adminHome, filterHomeSites, guestSheet, sopLibraryScreen, sopReviewScreen, type AddSiteEntry } from "./admin-home-screens";
import { qewTagsPdf } from "./qew-tag-pdf";
import { closeQewPrint, onQewPrintClick, onQewPrintInput, openQewPrint, qewPrintHtml, qewPrintOpen } from "./qew-print";
import { cleanNewSite, newSiteProblem, plantsNotSites, qewTagIds } from "./sites";
import { masterName, masterSendName, outboxPath as driveOutboxPath } from "./drive-folders";
import { uploadDrivePdf } from "./firebase";
import { gloveRecord, glovesEnter, glovesRoster } from "./gloves-screens";
import { copyDateDown, enterProblem, enterRows, isFilled, rowsToSend, saveLabel, type EnterCell, type EnterRow } from "./gloves-enter";
import { usDate as gloveDate } from "./gloves";
import { emailLog, emailRecord, type TestState } from "./email-screens";
import { workRequestList, workRequestRecord, type ScreenState } from "./work-request-screens";
import { nearMissList, nearMissRecord, type NearMissScreen } from "./near-miss-screens";
import { visitList, visitRecord, type VisitScreen } from "./contractor-screens";
import { domainsProblem, parseDomains, sponsorParamsFrom } from "./contractor";
import { mountSponsorPage } from "./sponsor-page";
import { scanCountLine, type ScanCountFrom } from "./scan-log";
import {
  classChanges,
  classProblem,
  fieldValueProblem,
  plantProblem,
  slug as listSlug,
  groupValues,
  sortClasses,
  type ClassInput,
  type FieldValueInput,
  type PlantInput,
} from "./lists";
import {
  classDetail,
  classForm,
  classList,
  classSaveSheet,
  fieldList,
  fieldListsIndex,
  fieldValueForm,
  itemActiveSheet,
  listNameFromSlug,
  listsMenu,
  plantDeleteSheet,
  plantForm,
  plantList,
  type ListOrderView,
} from "./lists-screens";
import { retryRefused } from "./fresh-sign-in";
import { dragOrder } from "./drag-order";
import {
  ALL_SWITCHES,
  LIMITS,
  NO_PROGRAM_TICKS,
  PROGRAM_USED_FOR,
  limitProblem,
  limitValue,
  programContactProblem,
  programInfoProblem,
  type LimitField,
  type ProgramContactInput,
  type SwitchField,
} from "./settings";
import {
  eewpPage,
  limitForm,
  limitSheet,
  limitsPage,
  lotoPage,
  programContactForm,
  programContactsList,
  programForm,
  settingsMenu,
  type ProgramEntry,
  switchSheet as settingSwitchSheet,
} from "./settings-screens";
import {
  groupPeople,
  lotoPending,
  newPersonProblem,
  allNodesDoneSet,
  authorityLabel,
  isAdminId,
  nodesOneToThreeDoneSet,
  outOfServiceProblem,
  personProblem,
  rowsFor,
  verifyProblem,
  type BadgeDoc,
  type NewPersonInput,
  type PersonInput,
  type TagBucket,
} from "./people";
import {
  activeSheetPerson,
  addPersonForm,
  authoritySheet,
  outOfServiceForm,
  peopleGroups,
  peopleShell,
  personDetail,
  personEditForm,
  tagList,
  siteTags,
  tagsOverview,
  verifyLotoForm,
} from "./people-screens";
import {
  NO_TICKS,
  USED_FOR,
  contactProblem,
  groupAssets,
  siteContacts,
  siteDetailsProblem,
  usedForForRole,
  type AssetRow,
  type ContactInput,
  type SiteDetailsInput,
} from "./sites";
import {
  activeSheet,
  assetGroups,
  assetListShell,
  type ScanState,
  contactForm,
  demoDetail,
  demoList,
  siteEditForm,
} from "./sites-screens";
import { findSop, reviewHtml, sopLibrary } from "./resources";
import { canSaveNewTask, changeLine, changeProblem, isTaskType, newTaskProblem, reasonProblem, type NewTaskInput } from "./tasks";
import { addTaskForm, changeSheet, groupsFor, switchSheet as taskSwitchSheet, taskDetail, taskSopView, tasksShell } from "./tasks-screens";
import { formReady as refFormReady, fieldsProblem as refFieldsProblem, isKind, newEditionProblem, newRefProblem, nextRefId, reasonProblem as refReasonProblem, type FileInfo, type RefInput } from "./references";
import { editionSheet, formNote, groupsHtml as refGroupsHtml, refDetail, refEditForm, refForm, refsShell, retireSheet, type FormMode } from "./references-screens";
import type { NodeId, QuizId } from "./training";
import { dateInSiteTime, questionProblem, type FailedInSession, type QuestionInput } from "./training-server";
import {
  errorScreen,
  loadingScreen,
  noBadgeScreen,
  portalClass,
  portalTrainee,
  quizBankList,
  quizQuestionDetail,
  signInScreen,
  topBar,
} from "./training-screens";
import { SITE_NAMES, classResults, quizIdFrom, rosterForSite, setLiveSiteNames, siteNameOf as trainingSiteName, sitesWithCounts, summarizeQuizzes } from "./training-view";

console.log(`SafeTag ${__APP_VERSION__}`);

const root = document.querySelector<HTMLElement>("#app")!;
let badge: SignInClaims | null = null;
let data: TrainingData | null = null;
/** The training data read failed. The page shows why and Try Again (NAV_AUDIT.md item 5), never loading for good. */
let dataError = "";
let dataLoading = false;

async function loadTrainingNow(): Promise<void> {
  if (!badge || dataLoading) return;
  dataLoading = true;
  dataError = "";
  try {
    // The first read after a fresh sign-in can be refused for a moment: it tries again on its own (1:12 PM).
    const company = badge.company;
    // A time limit, so a stalled store shows Try Again instead of loading for good (field test 2026-10-29 8:49 AM).
    data = await withTimeLimit(retryRefused(() => loadTraining(company)));
  } catch (e) {
    dataError = `Could not read training data: ${messageOf(e)}`;
  } finally {
    dataLoading = false;
  }
  render();
}
// Set on a quiz link. The quiz page owns the screen and its own events then.
const quizLink = quizParamsFrom(location.search);
// Set on an EEWP approver's link (EEWP_WORDS.md rev 3). Review And Decide owns the screen. No sign-in.
const decisionLink = decisionParamsFrom(location.search);
// Set on a contractor sponsor's link (OBSERVATIONS_SCAN_WORDS.md rev 3). Confirm or the Daily Check. No sign-in.
const sponsorLink = sponsorParamsFrom(location.search);
// Set on an asset label. The guest page owns the screen and its own events then.
const linkParams = new URLSearchParams(location.search);
const scanLink = quizLink || (!linkParams.has("aid") && !linkParams.has("tag")) ? null : readQrLink(location.href);
const guestLink = scanLink && !(scanLink.kind === "badge" && isAdminBadgeId(scanLink.badgeId)) ? scanLink : null;

// ---- Portal state ----

type Sheet =
  | { kind: "end"; site: string; failed: FailedInSession[]; ticked: boolean; busy: boolean; error: string }
  | { kind: "reopen"; site: string; sessionId: string; busy: boolean; error: string; reason: string; context: string }
  | { kind: "switch"; questionId: string; busy: boolean; error: string }
  | { kind: "passmark"; quizId: QuizId; value: string; busy: boolean; error: string }
  | { kind: "siteActive"; site: string; busy: boolean; error: string }
  | { kind: "siteZone"; site: string; picked: string; busy: boolean; error: string }
  | { kind: "auth"; qewId: string; key: Authority; isOn: boolean; busy: boolean; error: string }
  | { kind: "personActive"; qewId: string; busy: boolean; error: string }
  | { kind: "settingSwitch"; field: SwitchField; isOn: boolean; busy: boolean; error: string }
  | { kind: "limit"; field: LimitField; from: number; to: number; busy: boolean; error: string }
  | { kind: "classSave"; code: string; entry: ClassInput; changes: string[]; busy: boolean; error: string }
  | { kind: "itemActive"; itemKind: "class" | "value"; id: string; name: string; isOn: boolean; busy: boolean; error: string }
  | { kind: "plantDelete"; siteNumber: string; busy: boolean; error: string }
  | { kind: "taskSwitch"; taskId: string; isOn: boolean; reason: string; busy: boolean; error: string }
  | { kind: "taskChange"; taskId: string; field: "tier" | "taskType"; to: string; reason: string; busy: boolean; error: string }
  | { kind: "refEdition"; refId: string; busy: string; error: string }
  | { kind: "refRetire"; refId: string; reason: string; busy: boolean; error: string }
  | { kind: "guest"; site: string; key: DemoLabelKey; busy: false }
  | { kind: "addSite"; entry: AddSiteEntry; busy: boolean; error: string; step: string };

/** Sessions per site. A string is the read error. */
const sessions = new Map<string, SiteSessions | string>();
const loadingSites = new Set<string>();
let sheet: Sheet | null = null;
/** A one-line result after a button, shown on the page it belongs to. */
let flash: { route: string; text: string; tone: "green" | "red" } | null = null;
/** Close by Hand or Edit Completion Date in progress, kept so a refusal keeps what was typed. */
let form: { route: string; entry: CloseByHandEntry | null; date: string | null; busy: boolean; error: string } | null = null;
/** Add or Change a question in progress, kept so a refusal keeps what was typed. */
let qform: { route: string; entry: QuestionInput & { type: "MC" | "TF" }; busy: boolean; error: string } | null = null;
let acting = false;

// ---- Sites page state ----
/** Sites, counts, contacts and the Demo set. A string is the read error. */
let sitesData: SitesData | string | null = null;
let sitesLoading = false;
/** Assets per site, read when the asset list or a site first opens. A string is the read error. */
const siteAssets = new Map<string, AssetRow[] | string>();
const assetsLoading = new Set<string>();
/** What is typed in the asset search, per site, so Back and forth keeps it. */
const assetSearch = new Map<string, string>();
/** Site details or a contact in progress, kept so a refusal keeps what was typed. */
let siteForm: { route: string; entry: SiteDetailsInput; busy: boolean; error: string } | null = null;
let contactEdit: { route: string; entry: ContactInput; busy: boolean; error: string } | null = null;
/** Site Hub (COHESION_WORDS.md Move 1): each site's program, read when its hub opens. A string is the read error. */
const hubProgram = new Map<string, ProgramData | string>();
const hubLoading = new Set<string>();
/** Outbox items waiting on this device, per site. Null until counted. */
const hubWaiting = new Map<string, number>();

// ---- People & Badges state ----
/** Badges, people, site names and node records. A string is the read error. */
let peopleData: PeopleData | string | null = null;
let peopleLoading = false;
let peopleSearch = "";
/** Forms in progress, kept so a refusal keeps what was typed. */
let personEdit: { route: string; entry: PersonInput; busy: boolean; error: string } | null = null;
let addEdit: { route: string; entry: NewPersonInput; busy: boolean; error: string } | null = null;
let outEdit: { route: string; entry: { reason: string; note: string }; busy: boolean; error: string } | null = null;
let verifyEdit: { route: string; entry: string; busy: boolean; error: string } | null = null;

// ---- Program Settings state ----
let settingsData: SettingsData | string | null = null;
let settingsLoading = false;
let programEdit: { route: string; entry: ProgramEntry; busy: boolean; error: string } | null = null;
let pcEdit: { route: string; entry: ProgramContactInput; busy: boolean; error: string } | null = null;
/** What was typed in a limit, kept while the confirm is open or after a refusal. */
let limitEdit: { route: string; entry: string; error: string } | null = null;

// ---- Gloves roster state ----
let glovesData: GlovesData | string | null = null;
let glovesLoading = false;

// ---- Lists state ----
let listsData: ListsData | string | null = null;
let listsLoading = false;
let classEdit: { route: string; entry: ClassInput; error: string } | null = null;
/**
 * Order by drag and drop on the Lists page (LISTS_DRAG_B3_WORDS.md). Nothing saves while dragging; Done sends each
 * group that changed. `start` is the order when Order was tapped, for Cancel and to know what changed.
 */
let listOrder: { route: string; kind: "class" | "value"; listName: string; view: ListOrderView; start: Record<string, string[]> } | null = null;
let valueEdit: { route: string; entry: FieldValueInput; busy: boolean; error: string } | null = null;
let plantEdit: { route: string; entry: PlantInput; busy: boolean; error: string } | null = null;

// ---- Tasks state ----
let tasksData: TasksData | string | null = null;
let tasksLoading = false;
let taskSearch = "";
/** Add Task in progress, kept so a refusal keeps what was typed. */
let taskAdd: { route: string; entry: NewTaskInput; busy: boolean; error: string } | null = null;
/** Open SOP: the review copy of the file, per task. Revoked when the page leaves it. */
let taskSop: { taskId: string; frameUrl: string | null; error: string } | null = null;

const EMPTY_TASK: NewTaskInput = { name: "", tier: null, taskType: "", sopNumber: "" };

// ---- Email Log state (EMAIL_SENDER_WORDS.md, 2026-10-10) ----
let emailData: EmailData | string | null = null;
let emailLoading = false;
let emailTest: TestState = { busy: false, error: "", note: "" };
/** The list he opened an email from, so Back returns to it. */
let emailListHref = "#/email-log/all";
let wrData: WorkRequestData | string | null = null;
let wrLoading = false;
let wrListHref = "#/work-requests/all";
let wrScreen: ScreenState = { busy: false, error: "" };
// ---- Near Miss and Contractor Visits (piece 6) ----
let nmData: NearMissData | string | null = null;
let nmLoading = false;
let nmListHref = "#/near-miss/all";
let nmScreen: NearMissScreen = { busy: false, error: "" };
let cvData: VisitData | string | null = null;
let cvLoading = false;
let cvListHref = "#/contractors/all";
let cvScreen: VisitScreen = { busy: false, error: "" };
let cvSearch = "";
/** The Sites asset page: its scan count and the Reset sheet, per asset. */
const scanStates = new Map<string, ScanState>();

// ---- Reference Library state ----
let refsData: ReferencesData | string | null = null;
let refsLoading = false;
let refSearch = "";
/** Add, New Edition or Replace File in progress. The chosen file is kept here: a re-draw empties the file picker. */
let refAdd: { route: string; entry: RefInput; file: File | null; busy: string; error: string } | null = null;
let refEdit: { route: string; entry: { title: string; covers: string; notes: string }; busy: boolean; error: string } | null = null;

const EMPTY_REF: RefInput = { kind: "", title: "", number: "", edition: "", covers: "", notes: "" };

function route(): string[] {
  return location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
}

function routeKey(): string {
  return route().join("/");
}

function today(): string {
  return dateInSiteTime(new Date());
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function setFlash(text: string, tone: "green" | "red" = "green", at = routeKey()): void {
  flash = { route: at, text, tone };
}

/**
 * Opens or closes the bottom sheet. An open sheet is a Back step: the phone's Back gesture closes it,
 * like other apps (approved 2026-10-09). A busy sheet stays until its step finishes.
 */
function setSheet(next: Sheet | null): void {
  sheet = next;
  steps().set(sheet ? 1 : 0, () => {
    if (sheet?.busy) {
      setSheet(sheet);
      return;
    }
    sheet = null;
    render(true);
  });
}

/** The admin pages he moved through, by address, so Save can go up instead of adding a step. */
const hashTrail: string[] = [routeKey()];
let replacingHash = false;
function trackHash(): void {
  const k = routeKey();
  if (replacingHash) {
    replacingHash = false;
    hashTrail[hashTrail.length - 1] = k;
  } else if (hashTrail.length > 1 && hashTrail[hashTrail.length - 2] === k) hashTrail.pop();
  else if (hashTrail[hashTrail.length - 1] !== k) hashTrail.push(k);
}

/**
 * After Save, Delete or Add: back to the page above. When that page is the one he came from, this is
 * one step back, so Back afterward never reopens the form he just finished.
 */
function goUpTo(target: string): void {
  const t = target.replace(/^#?\/?/, "");
  if (t === routeKey()) {
    render();
    return;
  }
  if (hashTrail.length > 1 && hashTrail[hashTrail.length - 2] === t) history.back();
  else {
    replacingHash = true;
    location.replace(`#/${t}`);
  }
}

function page(inner: string, keepScroll = false): void {
  const isHome = routeKey() === "";
  const y = window.scrollY;
  root.innerHTML = `${topBar(badge?.holderName ?? "", !isHome, siteChip())}<div class="inner">${siteBackHtml()}${inner}</div>
    <footer class="foot">Build ${__APP_VERSION__}</footer>${sheetHtml()}${qewPrintCardHtml()}`;
  window.scrollTo(0, keepScroll ? y : 0);
}

/** Print QEW Tags (qew-print.ts), open only on a site's QEW Tags page. */
function qewPrintCardHtml(): string {
  if (!qewPrintOpen()) return "";
  const parts = route();
  if (parts[0] !== "people" || parts[1] !== "tags" || !parts[2] || parts[3] || typeof peopleData !== "object" || !peopleData) {
    closeQewPrint();
    return "";
  }
  return qewPrintHtml(peopleData.badges);
}

function sheetHtml(): string {
  if (!sheet) return "";
  if (sheet.kind === "guest") return guestSheet(homeSites(), sheet.site, sheet.key);
  if (sheet.kind === "addSite") {
    const sh = sheet;
    const plants = typeof listsData === "object" && listsData ? plantsNotSites(listsData.plants, typeof sitesData === "object" && sitesData ? sitesData.sites : []) : [];
    const lists = typeof listsData === "object" && listsData ? listsData : null;
    // The next number counts plant numbers too, so it waits for the Plants list (change 4).
    if (!sh.entry.fromPlant && !sh.busy) sh.entry.siteNumber = lists ? nextAddSiteNumber() : "";
    const plantName = lists ? (lists.plants.find((x) => x.siteNumber === sh.entry.siteNumber)?.name ?? "") : "";
    const note = lists ? addSiteNumberNote(sh.entry, plantName) : "Reading your Plants list…";
    return addSiteSheet(plants, sh.entry, sh.busy, sh.error, sh.step, note);
  }
  if (sheet.kind === "refEdition" || sheet.kind === "refRetire") {
    const sh = sheet;
    const d = typeof refsData === "object" ? refsData : null;
    const r = d?.refs.find((x) => x.refId === sh.refId);
    if (!d || !r) return "";
    if (sh.kind === "refRetire") return retireSheet(r, sh.reason, sh.busy, sh.error);
    return refAdd ? editionSheet(r, refAdd.entry, nextRefId(d.refs.map((x) => x.refId)), sh.busy, sh.error) : "";
  }
  if (sheet.kind === "taskSwitch" || sheet.kind === "taskChange") {
    const sh = sheet;
    const d = typeof tasksData === "object" ? tasksData : null;
    const t = d?.tasks.find((x) => x.taskId === sh.taskId);
    if (!d || !t) return "";
    if (sh.kind === "taskChange") return changeSheet(t, { field: sh.field, to: sh.to, reason: sh.reason }, sh.busy, sh.error);
    return taskSwitchSheet(t, sh.isOn, d.sops.some((s) => s.sopNumber === t.sopNumber), sh.reason, sh.busy, sh.error);
  }
  if (sheet.kind === "classSave") {
    const code = sheet.code;
    const c = typeof listsData === "object" ? listsData?.classes.find((x) => x.code === code) : undefined;
    return c ? classSaveSheet(c, sheet.changes, sheet.busy, sheet.error) : "";
  }
  if (sheet.kind === "itemActive") return itemActiveSheet(sheet.name, sheet.itemKind, sheet.isOn, sheet.busy, sheet.error);
  if (sheet.kind === "plantDelete") {
    const n = sheet.siteNumber;
    const p = typeof listsData === "object" ? listsData?.plants.find((x) => x.siteNumber === n) : undefined;
    return p ? plantDeleteSheet(p, sheet.busy, sheet.error) : "";
  }
  if (sheet.kind === "settingSwitch") {
    const f = sheet.field;
    const def = ALL_SWITCHES.find((x) => x.field === f);
    return def ? settingSwitchSheet(def, sheet.isOn, sheet.busy, sheet.error) : "";
  }
  if (sheet.kind === "limit") {
    const f = sheet.field;
    const def = LIMITS.find((x) => x.field === f);
    return def ? limitSheet(def, sheet.from, sheet.to, sheet.busy, sheet.error) : "";
  }
  if (sheet.kind === "auth" || sheet.kind === "personActive") {
    const sh = sheet;
    const d = typeof peopleData === "object" ? peopleData : null;
    const p = d?.people.find((x) => x.qewId === sh.qewId);
    if (!d || !p) return "";
    if (sh.kind === "auth") return authoritySheet(p, sh.key, sh.isOn, sh.busy, sh.error);
    const b = d.badges.find((x) => x["Badge ID"] === sh.qewId);
    return activeSheetPerson(p, b?.Status === "Active", sh.busy, sh.error);
  }
  if (sheet.kind === "siteActive") {
    const id = sheet.site;
    const site = typeof sitesData === "object" ? sitesData?.sites.find((x) => x.siteNumber === id) : undefined;
    return site ? activeSheet(site, sheet.busy, sheet.error) : "";
  }
  if (sheet.kind === "siteZone") {
    const id = sheet.site;
    const site = typeof sitesData === "object" ? sitesData?.sites.find((x) => x.siteNumber === id) : undefined;
    return site ? zoneSheet(site, sheet.picked, new Date(), sheet.busy, sheet.error) : "";
  }
  if (sheet.kind === "switch" || sheet.kind === "passmark") {
    if (!data) return "";
    const summaries = summarizeQuizzes(data.quizzes, data.questions);
    if (sheet.kind === "passmark") {
      const id = sheet.quizId;
      const qs = summaries.find((x) => x.quiz.quizId === id);
      return qs ? passMarkSheet(qs, sheet.value, sheet.busy, sheet.error) : "";
    }
    const id = sheet.questionId;
    const qs = summaries.find((x) => x.questions.some((q) => q.questionId === id));
    const q = qs?.questions.find((x) => x.questionId === id);
    return qs && q ? switchSheet(q, qs, sheet.busy, sheet.error) : "";
  }
  const s = sessions.get(sheet.site);
  if (!s || typeof s === "string") return "";
  if (sheet.kind === "end") return s.open ? endSessionSheet(s.open, sheet.failed, sheet.ticked, sheet.busy, sheet.error) : "";
  const id = sheet.sessionId;
  const past = s.past.find((p) => p.sessionId === id);
  return past ? reopenSheet(past, sheet.busy, sheet.error, sheet.reason, sheet.context) : "";
}

async function refreshSessions(site: string): Promise<void> {
  if (!badge) return;
  loadingSites.add(site);
  try {
    sessions.set(site, await loadSessions(badge.company, site));
  } catch (e) {
    sessions.set(site, messageOf(e));
  } finally {
    loadingSites.delete(site);
  }
}

async function refreshData(): Promise<void> {
  if (badge) data = await loadTraining(badge.company);
}

function render(keepScroll = false): void {
  if (!badge) return;
  noteLastStep();
  if (route()[0] === "survey" || route()[0] === "assets") {
    // The Asset ID Creator (#/assets) runs in the survey module: same site pack, Outbox and offline shell.
    const b = badge;
    renderSurvey({
      root,
      badge: b,
      frame: (above, inner) => {
        root.innerHTML = `${topBar(b.holderName, true, siteChip())}${above}<div class="inner">${siteBackHtml()}${inner}</div>
    <footer class="foot">Build ${__APP_VERSION__}</footer>`;
      },
    });
    return;
  }
  root.classList.remove("sv-large");
  root.classList.remove("ac-wide");
  root.classList.toggle("af-wide", route()[0] === "af-labels");
  if (!isOnline() && !opensOffline(location.hash)) {
    root.innerHTML = `${topBar(badge.holderName, routeKey() !== "")}${needsSignalScreen("Admin pages", true)}`;
    root.querySelector("[data-offline-survey]")?.addEventListener("click", () => (location.hash = "#/survey"));
    return;
  }
  if (route()[0] === "af-labels") {
    const b = badge;
    renderAfLabels({
      root,
      badge: b,
      frame: (inner) => {
        root.innerHTML = `${topBar(b.holderName, true, siteChip())}<div class="inner">${siteBackHtml()}${inner}</div>
    <footer class="foot">Build ${__APP_VERSION__}</footer>`;
      },
    });
    return;
  }
  if (route()[0] === "sites") {
    renderSites(keepScroll);
    return;
  }
  if (route()[0] === "people") {
    renderPeople(keepScroll);
    return;
  }
  if (route()[0] === "settings") {
    renderSettings(keepScroll);
    return;
  }
  if (route()[0] === "lists") {
    renderLists(keepScroll);
    return;
  }
  if (route()[0] === "gloves") {
    renderGloves(keepScroll);
    return;
  }
  if (route()[0] === "tasks") {
    renderTasks(keepScroll);
    return;
  }
  if (route()[0] === "email-log") {
    renderEmailLog(keepScroll);
    return;
  }
  if (route()[0] === "work-requests") {
    renderWorkRequests(keepScroll);
    return;
  }
  if (route()[0] === "near-miss") {
    renderNearMiss(keepScroll);
    return;
  }
  if (route()[0] === "contractors") {
    renderVisits(keepScroll);
    return;
  }
  if (route()[0] === "references") {
    renderReferences(keepScroll);
    return;
  }
  if (route()[0] === "sop-library") {
    renderSopLibrary(keepScroll);
    return;
  }
  if (!data) {
    if (dataError) page(`${errorScreen(dataError)}<button class="prim" type="button" data-act="training-retry">Try Again</button>`);
    else {
      page(loadingScreen("training data"));
      if (!dataLoading && isOnline()) void loadTrainingNow();
    }
    return;
  }
  const parts = route();
  const key = parts.join("/");
  const summaries = summarizeQuizzes(data.quizzes, data.questions);
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";

  if (parts[0] === "quiz-bank") {
    const quizId = quizIdFrom(parts[1]);
    const s = summaries.find((x) => x.quiz.quizId === quizId)!;
    if (parts[2] === "new" || parts[3] === "edit") {
      const q = parts[2] === "new" ? null : (s.questions.find((x) => x.questionId === parts[2]) ?? null);
      const f = qform?.route === key ? qform : null;
      page(questionForm(s, q, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }
    const q = parts[2] ? s.questions.find((x) => x.questionId === parts[2]) : undefined;
    page(q ? quizQuestionDetail(summaries, s, q, flashHere) : quizBankList(summaries, quizId, flashHere), keepScroll);
    return;
  }

  if (parts[0] === "training") {
    const site = parts[1] ?? "01";
    if (parts[2] === "new") {
      page(`<a class="back" href="#/training/${site}">Cancel</a><div id="nt-host"></div>`);
      const host = root.querySelector<HTMLElement>("#nt-host");
      if (host) {
        mountNewTrainee(host, {
          siteNumber: site,
          siteName: trainingSiteName(site),
          onAdded: async (tagId, line) => {
            await refreshData();
            const at = `training/${site}/${tagId}`;
            setFlash(line, "green", at);
            goUpTo(at);
          },
        });
      }
      return;
    }

    const roster = rosterForSite(data.records, site, data.qewTags, data.attempts, today(), data.stations, data.loto);

    if (parts[2] === "results" && parts[3]) {
      const sid = parts[3];
      const ss = sessions.get(site);
      if (ss === undefined && !loadingSites.has(site)) void refreshSessions(site).then(() => render(true));
      const known = typeof ss === "object" && ss ? ([ss.open, ...ss.past].find((x) => x?.sessionId === sid) ?? null) : null;
      page(classResultsScreen(known, site, sid, classResults(data.attempts, sid)), keepScroll);
      return;
    }

    const t = parts[2] ? roster.find((x) => x.qewId === parts[2]) : undefined;

    if (t && parts[3] === "close") {
      const nodeId = parts[4] as NodeId;
      const row = t.nodes.find((n) => n.nodeId === nodeId);
      if (!row || row.state !== "open") {
        page(`<a class="back" href="#/training/${site}/${t.qewId}">${escapeHtml(t.name)}</a>${errorScreen("That node is not open for Close by Hand. Nodes close in order.")}`);
        return;
      }
      const s = row.quizId ? summaries.find((x) => x.quiz.quizId === row.quizId) : undefined;
      const f = form?.route === key ? form : null;
      page(closeByHandForm(t, nodeId, s?.quiz.passMarkPct ?? 80, s?.activeCount ?? 0, today(), f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }

    if (t && parts[3] === "date") {
      const hit = t.nodes.find((n) => n.record?.recordId === parts[4] || n.expired?.recordId === parts[4]);
      const r = hit && hit.record?.recordId === parts[4] ? hit.record : (hit?.expired ?? undefined);
      if (!r) {
        page(`<a class="back" href="#/training/${site}/${t.qewId}">${escapeHtml(t.name)}</a>${errorScreen("That record was not found.")}`);
        return;
      }
      const f = form?.route === key ? form : null;
      page(editDateForm(t, r, today(), f?.date ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }

    if (t) {
      page(portalTrainee(t, flashHere), keepScroll);
      return;
    }

    // The class page names sites from the live list. Read it once if Home has not yet (SITE_GROWTH_FIXES_WORDS.md item 3).
    if (sitesData === null && !sitesLoading && badge?.authorities.includes("admin")) {
      void refreshSites().then(() => {
        if (route()[0] === "training") render(true);
      });
    }
    const s = sessions.get(site);
    if (s === undefined && !loadingSites.has(site)) {
      void refreshSessions(site).then(() => {
        if (route()[0] === "training" && (route()[1] ?? "01") === site && !route()[2]) render(true);
      });
    }
    const siteName = trainingSiteName(site);
    const ok = typeof s === "object" ? s : null;
    page(
      portalClass(sitesWithCounts(data.records), site, roster, {
        flash: flashHere,
        session: sessionBox(siteName, ok, typeof s === "string" ? s : "", ok?.open ? classResults(data.attempts, ok.open.sessionId) : null, site),
        past: pastSessionsBox(ok),
      }),
      keepScroll,
    );
    return;
  }

  renderHome(keepScroll);
}

// ---- Admin Home (Stage 3 item 7, ADMIN_HOME_WORDS.md) ----

/** Sites for the Guest Page sheet: from the Sites data once loaded, else the five known sites. */
function homeSites(): { siteNumber: string; name: string }[] {
  const d = typeof sitesData === "object" ? sitesData : null;
  if (d?.sites.length) return [...d.sites].sort((a, b) => a.siteNumber.localeCompare(b.siteNumber)).map((x) => ({ siteNumber: x.siteNumber, name: x.name }));
  return Object.entries(SITE_NAMES).map(([siteNumber, name]) => ({ siteNumber, name }));
}

let homeLoadsStarted = false;

/** Reads what Home counts, the same way each page reads it. Each part fills in as its data lands. */
function startHomeLoads(): void {
  const again = () => {
    if (routeKey() === "") render(true);
  };
  // Sites can be cleared by Try Again, so they are checked every time Home draws.
  if (sitesData === null && !sitesLoading) void refreshSites().then(again);
  if (homeLoadsStarted) return;
  homeLoadsStarted = true;
  if (peopleData === null && !peopleLoading) void refreshPeople().then(again);
  if (tasksData === null && !tasksLoading) void refreshTasks().then(again);
  if (settingsData === null && !settingsLoading) void refreshSettings().then(again);
  if (listsData === null && !listsLoading) void refreshLists().then(again);
  if (refsData === null && !refsLoading) void refreshRefs().then(again);
  if (emailData === null && !emailLoading) void refreshEmails().then(again);
}

let homeSearch = "";

function renderHome(keepScroll: boolean): void {
  startHomeLoads();
  const obj = <T,>(v: T | string | null): T | null => (typeof v === "object" ? v : null);
  const sites = obj(sitesData);
  const company = badge?.company ?? "";
  const input = {
    today: today(),
    sites,
    people: obj(peopleData) ?? obj(glovesData),
    tasks: obj(tasksData),
    settings: obj(settingsData),
    lists: obj(listsData),
    refs: obj(refsData),
    emails: obj(emailData),
    nowIso: new Date().toISOString(),
    strips: company ? readJsonMap<string>(readLocal(stripKey(company))) : {},
    training: data,
    failed: [
      typeof tasksData === "string" ? "TK" : "",
      typeof tasksData === "string" ? "SL" : "",
      typeof settingsData === "string" ? "PS" : "",
      typeof listsData === "string" ? "LS" : "",
      typeof refsData === "string" ? "RL" : "",
      typeof emailData === "string" ? "EM" : "",
      typeof sitesData === "string" ? "DS" : "",
    ].filter(Boolean),
  };
  const names: Record<string, string> = { ...SITE_NAMES };
  for (const x of sites?.sites ?? []) names[x.siteNumber] = x.name;
  const last = company ? homeContinue(readJsonMap<LastStep>(readLocal(lastStepKey(company))), names) : null;
  const flashHere = flash && flash.route === "" ? flashNote(flash) : "";
  page(
    adminHome({
      holderName: badge?.holderName ?? "Admin",
      sub: homeSubLine(input),
      cont: last ? { href: last.href, siteName: last.siteName, name: last.name, when: whenLine(last.at, input.nowIso) } : null,
      sitesHead: sitesHeadLine(input),
      sites: homeSiteRows(input),
      sitesError: typeof sitesData === "string" ? sitesData : "",
      search: homeSearch,
      groups: homeGroups(input),
      flash: flashHere,
      addSite: badge?.sites.includes("ALL") ? (isOnline() ? "on" : "offline") : "",
      refresh: !sites || !sites.sites.length ? "" : homeRefreshing ? "busy" : isOnline() ? "on" : "offline",
      updated: updatedLine(sites, input.nowIso),
    }),
    keepScroll,
  );
  drawHomeSites();
  wirePullToRefresh();
}

// ---- Refresh on Home (COHESION_CHANGE_LOG.md change 1, approved 2026-10-24) ----

let homeRefreshing = false;

/** "Updated Today 5:34 AM": the oldest stage a site record holds, so it never claims more than is true. */
function updatedLine(sites: SitesData | null, nowIso: string): string {
  const at = sites ? oldestSavedAt(sites.sites.map((x) => x.stage)) : null;
  return at ? `Updated ${whenLine(at, nowIso)}` : "";
}

/** Server code works out every site's stage from the records and saves it. The rows update in place. */
async function refreshHomeStages(): Promise<void> {
  if (homeRefreshing || !isOnline() || typeof sitesData !== "object" || !sitesData) return;
  homeRefreshing = true;
  if (flash?.route === "") flash = null;
  render(true);
  try {
    const res = await callServer<{ stages: Record<string, { strip: string; savedAt: string; savedBy: string; savedByName: string }> }>("refreshSiteStages", {});
    if (typeof sitesData === "object" && sitesData) {
      for (const s of sitesData.sites) {
        const st = res.stages[s.siteNumber];
        if (st) {
          s.stage = st;
          stageSent.set(s.siteNumber, st.strip);
        }
      }
    }
  } catch (e) {
    setFlash(`Could not refresh: ${messageOf(e)}`, "red");
  } finally {
    homeRefreshing = false;
    if (routeKey() === "") render(true);
  }
}

/** Pull down on the site list does what Refresh does, the iPhone way. Only from the top of the page. */
let pullWired = false;
function wirePullToRefresh(): void {
  if (pullWired) return;
  pullWired = true;
  let startY = -1;
  let pulled = false;
  const bar = () => document.getElementById("home-pull");
  root.addEventListener("touchstart", (e) => {
    startY = routeKey() === "" && window.scrollY <= 0 && (e.target as Element).closest?.("#home-sites") ? e.touches[0]!.clientY : -1;
    pulled = false;
  }, { passive: true });
  root.addEventListener("touchmove", (e) => {
    if (startY < 0 || homeRefreshing) return;
    pulled = e.touches[0]!.clientY - startY > 70;
    bar()?.classList.toggle("on", pulled);
  }, { passive: true });
  root.addEventListener("touchend", () => {
    if (startY < 0) return;
    startY = -1;
    if (pulled && !homeRefreshing) void refreshHomeStages();
    else bar()?.classList.remove("on");
    pulled = false;
  });
}

/** One past the highest site or plant number (COHESION_CHANGE_LOG.md change 4). Blank when all 100 are used. */
function nextAddSiteNumber(): string {
  const sites = liveSiteNumbers() ?? [];
  const plants = typeof listsData === "object" && listsData ? listsData.plants.map((x) => x.siteNumber) : [];
  return nextSiteNumber([...sites, ...plants]) ?? "";
}

/** The live site numbers, once the sites are read. */
function liveSiteNumbers(): string[] | null {
  return typeof sitesData === "object" && sitesData ? sitesData.sites.map((x) => x.siteNumber) : null;
}

// ---- Add Site (ADD_SITE_WORDS.md, approved 2026-10-23) ----

/**
 * The QEW tag PDF into the outbox. The server copies it to 03 QEW Tags as the site's one tag PDF,
 * 10_QEW_Tags.pdf (list 2, 2026-10-25). At the roll's start offset, like every master.
 */
async function saveTagPdf(company: string, site: string, badgeId: string, byName: string): Promise<void> {
  const bytes = await qewTagsPdf(qewTagIds(site));
  const fileName = masterSendName(masterName("tags", site), Date.now());
  await uploadDrivePdf(driveOutboxPath(company, site, "tags", fileName), new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "application/pdf" }), { badgeId, byName });
}

async function confirmAddSite(): Promise<void> {
  if (sheet?.kind !== "addSite" || sheet.busy || !badge) return;
  const sh = sheet;
  const b = badge;
  const problem = newSiteProblem(sh.entry, liveSiteNumbers() ?? []);
  if (problem) {
    sh.error = problem;
    render(true);
    return;
  }
  const entry = cleanNewSite(sh.entry);
  sh.busy = true;
  sh.error = "";
  sh.step = "Making the site, its 99 QEW tags, its Drive folder and workbook…";
  render(true);
  try {
    const res = await callServer<{ siteNumber: string; name: string; tagsMade: number; drive: { state: string; reason: string; folderUrl: string; workbookNote: string } }>("addSite", { ...entry });
    sh.step = "Saving the QEW tag PDF…";
    render(true);
    let tagNote = "";
    try {
      await saveTagPdf(b.company, res.siteNumber, b.badgeId, b.holderName);
    } catch (e) {
      tagNote = `The QEW tag PDF did not save: ${messageOf(e)}`;
    }
    const lines = [`${res.siteNumber} ${res.name} is added, Off. ${res.tagsMade} QEW tags made.`];
    if (res.drive.state !== "done") lines.push(`Drive did not finish: ${res.drive.reason} Build Folder in the Assessment Sheet finishes it.`);
    if (res.drive.workbookNote) lines.push(res.drive.workbookNote);
    if (tagNote) lines.push(tagNote);
    const ok = res.drive.state === "done" && !res.drive.workbookNote && !tagNote;
    sh.busy = false;
    setSheet(null);
    setFlash(lines.join(" "), ok ? "green" : "red", `sites/${res.siteNumber}`);
    sitesData = null;
    listsData = null;
    // Every list that names the sites or holds the badges reads again, so the new site and its tags show
    // without a reload (SITE_GROWTH_FIXES_WORDS.md item 1).
    peopleData = null;
    glovesData = null;
    emailData = null;
    wrData = null;
    nmData = null;
    cvData = null;
    dropSurveySites();
    dropAfSites();
    homeLoadsStarted = false;
    await refreshSites();
    location.hash = `#/sites/${res.siteNumber}`;
  } catch (e) {
    sh.busy = false;
    sh.step = "";
    sh.error = messageOf(e);
    render(true);
  }
}

function drawHomeSites(): void {
  const host = root.querySelector<HTMLElement>("#home-sites");
  if (!host) return;
  const shown = filterHomeSites(host, homeSearch);
  const none = root.querySelector<HTMLElement>("#home-sites-none");
  if (none) none.hidden = shown > 0 || !host.querySelector(".hm-site");
}

// ---- SOP Library, admin review (Field Pages tile) ----

let sopReview: { sopNumber: string; frameUrl: string | null; error: string } | null = null;

function dropSopReview(): void {
  if (sopReview?.frameUrl) URL.revokeObjectURL(sopReview.frameUrl);
  sopReview = null;
}

async function openSopReview(sopNumber: string, fileUrl: string): Promise<void> {
  dropSopReview();
  sopReview = { sopNumber, frameUrl: null, error: "" };
  try {
    const r = await fetch(fileUrl);
    if (!r.ok) throw new Error("The SOP page did not load.");
    const html = await r.text();
    if (sopReview?.sopNumber !== sopNumber) return;
    sopReview = { sopNumber, frameUrl: URL.createObjectURL(new Blob([reviewHtml(html)], { type: "text/html" })), error: "" };
  } catch (e) {
    if (sopReview?.sopNumber === sopNumber) sopReview = { sopNumber, frameUrl: null, error: `Could not open the SOP. ${messageOf(e)}` };
  }
  if (route()[0] === "sop-library" && route()[1]) render(true);
}

function renderSopLibrary(keepScroll: boolean): void {
  const d = needTasks(keepScroll, "sop-library");
  if (!d) return;
  const lib = sopLibrary(d.sops, d.tasks);
  const n = route()[1] ? decodeURIComponent(route()[1]!) : "";
  if (!n) {
    page(sopLibraryScreen(lib), keepScroll);
    return;
  }
  const s = findSop(lib, n);
  if (!s || !s.fileUrl) {
    page(`<a class="back" href="#/sop-library">SOP Library</a>${errorScreen(`${n} is not in the SOP Library a QEW sees.`)}`);
    return;
  }
  if (sopReview?.sopNumber !== n) void openSopReview(n, s.fileUrl);
  page(sopReviewScreen(s, sopReview?.frameUrl ?? null, sopReview?.error ?? ""), keepScroll);
}


// ---- Sites page (SITES_PAGE_WORDS.md, MOCKUP_SITES_APPROVED.html) ----

async function refreshSites(): Promise<void> {
  if (!badge) return;
  sitesLoading = true;
  try {
    sitesData = await loadSitesData(badge.company);
    setLiveSiteNames(sitesData.sites);
  } catch (e) {
    sitesData = `Could not read the sites: ${messageOf(e)}`;
  } finally {
    sitesLoading = false;
  }
}

async function refreshAssets(site: string): Promise<void> {
  if (!badge) return;
  assetsLoading.add(site);
  try {
    siteAssets.set(site, await loadSiteAssets(badge.company, site));
  } catch (e) {
    siteAssets.set(site, `Could not read the assets: ${messageOf(e)}`);
  } finally {
    assetsLoading.delete(site);
  }
}

/** Reads once, then draws again if the person is still on a Sites page. */
function needSites(keepScroll: boolean): SitesData | null {
  if (typeof sitesData === "object" && sitesData) return sitesData;
  if (typeof sitesData === "string") {
    page(`${errorScreen(sitesData)}<button class="prim" type="button" data-act="sites-retry">Try Again</button>`);
    return null;
  }
  if (!sitesLoading) {
    void refreshSites().then(() => {
      if (route()[0] === "sites") render(keepScroll);
    });
  }
  page(loadingScreen("sites"));
  return null;
}

function needAssets(site: string): AssetRow[] | string | null {
  const a = siteAssets.get(site);
  if (a !== undefined) return a;
  if (!assetsLoading.has(site)) {
    void refreshAssets(site).then(() => {
      const r = route();
      if (r[0] === "sites" && r[1] === site && (!r[2] || r[2] === "assets")) render(true);
    });
  }
  return null;
}

function drawAssetGroups(site: string): void {
  const host = root.querySelector<HTMLElement>("#asset-groups");
  const a = siteAssets.get(site);
  if (!host) return;
  if (a === undefined) {
    host.innerHTML = loadingScreen("assets");
    return;
  }
  if (typeof a === "string") {
    host.innerHTML = errorScreen(a);
    return;
  }
  const q = assetSearch.get(site) ?? "";
  host.innerHTML = assetGroups(groupAssets(a, q), q);
}

function renderSites(keepScroll: boolean): void {
  const d = needSites(keepScroll);
  if (!d) return;
  const parts = route();
  const key = parts.join("/");
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  const siteId = parts[1];

  // Home rev 3: Home is the site list. Old links to #/sites land on Home.
  if (!siteId) {
    location.replace("#/");
    return;
  }

  if (siteId === "demo") {
    const k = parts[2];
    const t = k ? d.demo[k] : undefined;
    if (k && !t) {
      page(`<a class="back" href="#/sites/demo">Demo Set</a>${errorScreen("That demo template was not found.")}`);
      return;
    }
    page(k && t ? demoDetail(k, t) : demoList(d), keepScroll);
    return;
  }

  const site = d.sites.find((x) => x.siteNumber === siteId);
  if (!site) {
    page(errorScreen(`Site ${siteId} was not found.`));
    return;
  }

  if (parts[2] === "edit") {
    const f = siteForm?.route === key ? siteForm : null;
    page(siteEditForm(site, f?.entry ?? null, today(), f?.busy ?? false, f?.error ?? ""), keepScroll);
    return;
  }

  if (parts[2] === "contacts" && parts[3]) {
    const contact = parts[3] === "new" ? null : (siteContacts(d.contacts, site.siteNumber).find((c) => c.contactId === parts[3]) ?? undefined);
    if (contact === undefined) {
      page(`<a class="back" href="#/sites/${site.siteNumber}">${escapeHtml(site.name)}</a>${errorScreen("That contact was not found at this site.")}`);
      return;
    }
    const f = contactEdit?.route === key ? contactEdit : null;
    page(contactForm(site, contact, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
    return;
  }

  if (parts[2] === "assets" && parts[3]) {
    const assetId = decodeURIComponent(parts[3]);
    const list = needAssets(site.siteNumber);
    if (list === null) {
      page(loadingScreen("the asset"));
      return;
    }
    const asset = Array.isArray(list) ? list.find((x) => x.assetId === assetId) : undefined;
    if (!asset) {
      page(`<a class="back" href="#/sites/${site.siteNumber}/assets">Asset List</a>${errorScreen(typeof list === "string" ? list : `${assetId} is not in this site's asset list.`)}`);
      return;
    }
    // One asset page (ONE_ASSET_PAGE_WORDS.md): the whole row, read only, from the cloud.
    const got = needAssetPage(site.siteNumber, assetId);
    if (got === null) {
      page(`<a class="back" href="#/sites/${site.siteNumber}/assets">Asset List</a>${loadingScreen("the asset")}`);
      return;
    }
    if (typeof got === "string") {
      page(`<a class="back" href="#/sites/${site.siteNumber}/assets">Asset List</a>${errorScreen(got)}<button class="prim" type="button" data-act="asset-page-retry">Try Again</button>`);
      return;
    }
    if (wrData === null && !wrLoading) void refreshWorkRequests().then(() => render(true));
    if (nmData === null && !nmLoading) void refreshNearMisses().then(() => render(true));
    const records: AssetPageData["records"] = [
      ...(typeof wrData === "object" && wrData ? wrData.rows : [])
        .filter((r) => r.assetId === assetId && (r.status === "Open" || r.status === "In Work"))
        .map((r) => ({ kind: "Work Request" as const, id: r.requestId, line: r.lines.map((l) => l.finding).join(", "), href: `#/work-requests/r/${encodeURIComponent(r.requestId)}`, priority: String(r.priority ?? "") })),
      ...(typeof nmData === "object" && nmData ? nmData.rows : [])
        .filter((r) => r.assetId === assetId && r.status === "Open")
        .map((r) => ({ kind: "Near Miss" as const, id: r.observationId, line: r.picks.join(", ") || r.note, href: `#/near-miss/r/${encodeURIComponent(r.observationId)}`, priority: String(r.priority ?? "") })),
    ];
    const view = assetView({
      assetId,
      siteNumber: site.siteNumber,
      siteName: site.name,
      siteIds: Array.isArray(list) ? list.map((x) => x.assetId) : [],
      records,
      ...got,
    });
    page(assetPageScreen(view, scanStateOf(assetId), { canMap: !!badge?.authorities.includes("ieaWalk"), flash: flashHere }), keepScroll);
    return;
  }

  if (parts[2] === "assets") {
    const total = d.assetCounts[site.siteNumber] ?? 0;
    page(assetListShell(site, total, assetSearch.get(site.siteNumber) ?? ""), keepScroll);
    needAssets(site.siteNumber);
    drawAssetGroups(site.siteNumber);
    return;
  }

  page(siteHub(hubView(site, d, flashHere)), keepScroll);
}

// ---- Site Hub (COHESION_WORDS.md Move 1, MOCKUP_SITE_HUB.html rev 1) ----

function readLocal(k: string): string | null {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}

function writeLocal(k: string, v: string): void {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    // Storage full or blocked: the hub still works, the Continue line and the list dots are not kept.
  }
}

// ---- The site chip (COHESION_WORDS.md Move 2) ----
// The site you are on shows in the top bar. On a site's pages it goes to the site's hub; on the hub,
// to Home to change site (Home is the site list, rev 3). With no signal it hides: the hub needs signal.

const SITE_ROUTES = new Set(["sites", "assets", "survey", "af-labels", "training", "gloves", "work-requests", "near-miss", "contractors", "email-log"]);
const siteNames = new Map<string, string>();

/** The site the address is on, or blank. */
function siteOfRoute(): string {
  const r = route();
  return SITE_ROUTES.has(r[0] ?? "") && r[1] && /^\d{2}$/.test(r[1]) ? r[1] : "";
}

const currentSiteKey = (company: string) => `safetag.hub.currentSite.v1.${company}`;

/** The last site worked, so Training and Arc Flash Labels open on it from Home. */
function currentSite(): string {
  return badge ? (readLocal(currentSiteKey(badge.company)) ?? "") : "";
}

/**
 * Notes the site the address is on, so Training and Arc Flash Labels open on it from Home.
 * The site chip that used to sit in the top bar is gone: the back link names the site (change 5).
 */
function siteChip(): string {
  const n = siteOfRoute();
  if (!n || !badge) return "";
  if (currentSite() !== n) writeLocal(currentSiteKey(badge.company), n);
  return "";
}

/** The site's name for its back link: the Sites records first, then the names this session has seen. */
function siteNameOf(n: string): string {
  const known = typeof sitesData === "object" && sitesData ? sitesData.sites.find((x) => x.siteNumber === n)?.name : undefined;
  if (known) siteNames.set(n, known);
  const loaded = typeof glovesData === "object" && glovesData ? glovesData.siteNames[n] : typeof peopleData === "object" && peopleData ? peopleData.siteNames[n] : undefined;
  if (loaded) return loaded;
  // The fixed list reads "Baconton, GA". The back link is the site's name only.
  return siteNames.get(n) ?? SITE_NAMES[n]?.split(",")[0] ?? `Site ${n}`;
}

/**
 * The back link at the top left of every page opened from a hub row (COHESION_CHANGE_LOG.md change 5).
 * It names the site and goes to its hub. A list set to All Sites goes back Home.
 * Pages a level deeper keep their own back to the list they came from.
 */
function siteBackHtml(): string {
  const back = siteBackOf(route());
  if (!back) return "";
  // Home is the header's Home button. No second Home under it (Robert, 2026-10-26).
  if (back === "home") return "";
  return `<a class="back" href="#/sites/${back}">${escapeHtml(siteNameOf(back))}</a>`;
}

/** Remembers the step page just opened, for Continue on that site's hub. */
function noteLastStep(): void {
  if (!badge) return;
  const st = stepOfRoute(route());
  if (!st) return;
  const k = lastStepKey(badge.company);
  const all = readJsonMap<LastStep>(readLocal(k));
  all[st.site] = { step: st.step, name: st.name, href: location.hash, at: new Date().toISOString() };
  writeLocal(k, JSON.stringify(all));
}

function needHubProgram(site: Site): void {
  if (!badge || hubProgram.has(site.siteNumber) || hubLoading.has(site.siteNumber)) return;
  const b = badge;
  hubLoading.add(site.siteNumber);
  const again = () => {
    const r = route();
    if (r[0] === "sites" && r[1] === site.siteNumber && !r[2]) render(true);
  };
  void loadSiteProgram(b.company, { siteNumber: site.siteNumber, name: site.name }, b.authorities.includes("ieaWalk"))
    .then((p) => hubProgram.set(site.siteNumber, p))
    .catch((e) => hubProgram.set(site.siteNumber, `Could not read the site's program: ${messageOf(e)}`))
    .finally(() => {
      hubLoading.delete(site.siteNumber);
      again();
    });
  void deviceList
    .records()
    .then((rows) => {
      const prefix = `companies/${b.company}/sites/${site.siteNumber}/`;
      hubWaiting.set(site.siteNumber, rows.filter((x) => x.path.startsWith(prefix) && x.state !== "sent").length);
    })
    .catch(() => hubWaiting.set(site.siteNumber, 0))
    .finally(again);
}

/** People, gloves and records come from the same reads Home makes. */
function startHubLoads(): void {
  const again = () => {
    const r = route();
    if (r[0] === "sites" && r[1] && !r[2]) render(true);
  };
  if (glovesData === null && !glovesLoading)
    void refreshGloves().then(() => {
      if (peopleData === null && typeof glovesData === "object" && glovesData) {
        const { gloves: _g, inspections: _i, ...people } = glovesData;
        peopleData = people;
      }
      again();
    });
  if (wrData === null && !wrLoading) void refreshWorkRequests().then(again);
  if (nmData === null && !nmLoading) void refreshNearMisses().then(again);
  if (cvData === null && !cvLoading) void refreshVisits().then(again);
}

function hubView(site: Site, d: SitesData, flashHere: string): Parameters<typeof siteHub>[0] {
  needHubProgram(site);
  startHubLoads();
  const b = badge!;
  const obj = <T,>(v: T | string | null): T | null => (typeof v === "object" ? v : null);
  const gl = obj(glovesData);
  const people = obj(peopleData) ?? gl;
  const packRaw = readLocal(`st_survey_pack_${b.company}_${site.siteNumber}`);
  let packSavedAt: string | null = null;
  try {
    packSavedAt = packRaw ? String((JSON.parse(packRaw) as { savedAt?: string }).savedAt ?? "") || null : null;
  } catch {
    packSavedAt = null;
  }
  const fl = loadFieldLabels(readLocal, b.company, site.siteNumber).data;
  let flAt = "";
  try {
    flAt = String((JSON.parse(readLocal(flStorageKey(b.company, site.siteNumber)) ?? "{}") as { savedAt?: string }).savedAt ?? "");
  } catch {
    flAt = "";
  }
  const nowIso = new Date().toISOString();
  const program = hubProgram.get(site.siteNumber) ?? null;
  const input: HubInput = {
    site,
    contacts: d.contacts,
    program,
    fieldLabels: fl.rooms.length ? fl : null,
    fieldLabelsSavedAt: flAt,
    device: { packSavedAt, waiting: hubWaiting.get(site.siteNumber) ?? null },
    driveRoot: null,
    badges: people ? people.badges : null,
    roster: gl ? rosterFor(gl) : null,
    workRequests: obj(wrData)?.rows ?? null,
    nearMisses: obj(nmData)?.rows ?? null,
    visits: obj(cvData)?.rows ?? null,
    nowIso,
  };
  const steps = hubSteps(input);
  // Home shows this stage. Kept on this device, and sent to the site record so every device sees it
  // (STAGE_CLOUD_WORDS.md). Home cannot afford 47 full reads.
  if (typeof program === "object" && program) {
    const strip = stripOf(steps);
    const k = stripKey(b.company);
    const all = readJsonMap<string>(readLocal(k));
    all[site.siteNumber] = strip;
    writeLocal(k, JSON.stringify(all));
    if (stageReady(input)) sendStage(site, strip);
  }
  const last = readJsonMap<LastStep>(readLocal(lastStepKey(b.company)))[site.siteNumber] ?? null;
  return {
    site,
    assetCount: d.assetCounts[site.siteNumber] ?? 0,
    steps,
    people: peopleHere(input),
    records: recordsHere(input),
    contacts: hubContacts(input),
    device: deviceCard(input.device),
    last: last ? { ...last, when: whenLine(last.at, nowIso) } : null,
    programError: typeof program === "string" ? program : "",
    flash: flashHere,
    markBusy,
  };
}

// ---- Done checks on the hub, 00 to 04 (COHESION_CHANGE_LOG.md change 2, approved 2026-10-24) ----

/** The step whose check is being saved. One at a time. */
let markBusy = "";

/** Tap: Done. Tap again: back. No confirm window. The hub then sends the new stage as it does today. */
async function markStep(step: string): Promise<void> {
  const r = route();
  const siteNumber = r[0] === "sites" ? (r[1] ?? "") : "";
  if (markBusy || !isOnline() || !siteNumber || typeof sitesData !== "object" || !sitesData) return;
  const site = sitesData.sites.find((x) => x.siteNumber === siteNumber);
  if (!site) return;
  const marks = (site.stepsDone ?? {}) as Record<string, unknown>;
  const done = !marks[step];
  markBusy = step;
  // A new try clears the last message here (NAV_CRAWL_WORDS.md A2): a check that saves never sits under an old error.
  if (flash?.route === routeKey()) flash = null;
  render(true);
  try {
    const res = await callServer<{ step: string; done: boolean; mark: { at: string; badgeId: string; holderName: string } | null }>("markStepDone", { siteNumber, step, done });
    const next = { ...marks };
    if (res.mark) next[res.step] = res.mark;
    else delete next[res.step];
    site.stepsDone = next;
  } catch (e) {
    setFlash(`Could not save the check: ${messageOf(e)}`, "red");
  } finally {
    markBusy = "";
    render(true);
  }
}

/** Strips sent this session, per site, so a redraw never sends twice. */
const stageSent = new Map<string, string>();

/** Sends the site's stage when it changed from what the site record holds. Quiet: Home still has this device's copy. */
function sendStage(site: Site, strip: string): void {
  if (!isOnline() || site.stage?.strip === strip || stageSent.get(site.siteNumber) === strip) return;
  stageSent.set(site.siteNumber, strip);
  void callServer<{ strip: string; unchanged: boolean }>("saveSiteStage", { siteNumber: site.siteNumber, strip })
    .then(() => {
      if (typeof sitesData === "object" && sitesData) {
        const s = sitesData.sites.find((x) => x.siteNumber === site.siteNumber);
        if (s) s.stage = { strip, savedAt: new Date().toISOString(), savedBy: badge?.badgeId ?? "", savedByName: badge?.holderName ?? "" };
      }
    })
    .catch(() => stageSent.delete(site.siteNumber));
}

function siteNumberNow(): string {
  return route()[1] ?? "";
}

/** Save the Time Zone picked on the sheet (SITE_TIME_ZONE_WORDS.md). Only the zone is sent. */
async function saveSiteZone(): Promise<void> {
  if (sheet?.kind !== "siteZone" || typeof sitesData !== "object" || !sitesData) return;
  const sh = sheet;
  const site = sitesData.sites.find((x) => x.siteNumber === sh.site);
  if (!site || !sh.picked) return;
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    // The zone alone (NAV_CRAWL_WORDS.md A1): a site with no city or state can still take its zone.
    await callServer("saveSiteDetails", { siteNumber: site.siteNumber, timeZone: sh.picked, zoneOnly: true });
    setSheet(null);
    setFlash(`${site.name} is on ${zoneInfo(sh.picked).name} time.`);
    await refreshSites();
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

async function confirmSiteActive(): Promise<void> {
  if (sheet?.kind !== "siteActive" || typeof sitesData !== "object" || !sitesData) return;
  const sh = sheet;
  const site = sitesData.sites.find((x) => x.siteNumber === sh.site);
  if (!site) return;
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const res = await callServer<{ name: string; isActive: boolean; unchanged: boolean }>("setSiteActive", {
      siteNumber: site.siteNumber,
      isActive: !site.isActive,
      confirmed: true,
    });
    setSheet(null);
    setFlash(
      res.unchanged
        ? `${res.name} was already ${res.isActive ? "active" : "off"}.`
        : res.isActive
          ? `${res.name} is active. Labels there open the full app.`
          : `${res.name} is off. Labels there open the guest page.`,
    );
    await refreshSites();
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

async function submitSiteForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const val = (n: string) => ((f.elements.namedItem(n) as HTMLInputElement | null)?.value ?? "").trim();
  const entry: SiteDetailsInput = { name: val("name"), city: val("city"), state: val("state").toUpperCase(), arcFlashAssessedOn: val("arcFlashAssessedOn") };
  const site = f.dataset.site ?? "";
  const problem = siteDetailsProblem(entry, today());
  if (problem) {
    siteForm = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  siteForm = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    await callServer("saveSiteDetails", { siteNumber: site, ...entry });
    await refreshSites();
    siteForm = null;
    const back = `sites/${site}`;
    setFlash("Details saved.", "green", back);
    goUpTo(back);
  } catch (e) {
    siteForm = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

function readContactForm(f: HTMLFormElement): ContactInput {
  const val = (n: string) => ((f.elements.namedItem(n) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "").trim();
  const on = (n: string) => Boolean((f.elements.namedItem(n) as HTMLInputElement | null)?.checked);
  const isEdit = Boolean(f.dataset.contact);
  const usedFor = { ...NO_TICKS };
  for (const u of USED_FOR) usedFor[u.key] = on(u.key);
  return { name: val("name"), email: val("email"), phone: val("phone"), role: val("role"), isActive: isEdit ? on("isActive") : true, usedFor };
}

async function submitContactForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const entry = readContactForm(f);
  const site = f.dataset.site ?? "";
  const contactId = f.dataset.contact ?? "";
  const current = contactId && typeof sitesData === "object" && sitesData ? sitesData.contacts.find((c) => c.contactId === contactId) : undefined;
  const problem = contactProblem(entry, current?.role ?? "");
  if (problem) {
    contactEdit = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  contactEdit = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ contactId: string; name: string; added: boolean }>("saveContact", {
      siteNumber: site,
      ...(contactId ? { contactId } : {}),
      ...entry,
    });
    await refreshSites();
    // The LOTO page on Program Settings counts site verifiers and awareness contacts.
    settingsData = null;
    contactEdit = null;
    const back = `sites/${site}`;
    setFlash(res.added ? `${res.name} added as ${res.contactId}.` : `${res.name} saved.`, "green", back);
    goUpTo(back);
  } catch (e) {
    contactEdit = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

// ---- People & Badges (PEOPLE_BADGES_WORDS.md, MOCKUP_PEOPLE_BADGES_APPROVED.html) ----

async function refreshPeople(): Promise<void> {
  if (!badge) return;
  peopleLoading = true;
  try {
    peopleData = await loadPeopleData(badge.company);
  } catch (e) {
    peopleData = `Could not read people and badges: ${messageOf(e)}`;
  } finally {
    peopleLoading = false;
  }
}

function needPeople(keepScroll: boolean): PeopleData | null {
  if (typeof peopleData === "object" && peopleData) return peopleData;
  if (typeof peopleData === "string") {
    page(`${errorScreen(peopleData)}<button class="prim" type="button" data-act="people-retry">Try Again</button>`);
    return null;
  }
  if (!peopleLoading) {
    void refreshPeople().then(() => {
      if (route()[0] === "people") render(keepScroll);
    });
  }
  page(loadingScreen("people and badges"));
  return null;
}

function pendingLotoCount(d: PeopleData): number {
  return d.people.filter((p) => !p.qewId.startsWith("ALL-") && lotoPending(p)).length;
}

function drawPeopleGroups(): void {
  const host = root.querySelector<HTMLElement>("#people-groups");
  const d = typeof peopleData === "object" ? peopleData : null;
  if (!host || !d) return;
  const badges = new Map<string, BadgeDoc>(d.badges.map((b) => [b["Badge ID"], b]));
  const rows = rowsFor(d.people, badges, allNodesDoneSet(d.records));
  const only = route()[1] === "site" ? (route()[2] ?? "") : "";
  const groups = groupPeople(rows, peopleSearch, route()[1] === "loto");
  host.innerHTML = peopleGroups(only ? groups.filter((g) => g.siteNumber === only) : groups, d, peopleSearch);
}

function renderPeople(keepScroll: boolean): void {
  const d = needPeople(keepScroll);
  if (!d) return;
  const parts = route();
  const key = parts.join("/");
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";

  if (!parts[1] || parts[1] === "loto") {
    page(peopleShell(d, pendingLotoCount(d), parts[1] === "loto", peopleSearch, flashHere), keepScroll);
    drawPeopleGroups();
    return;
  }

  // One site's people, opened from its hub (Home rev 3).
  if (parts[1] === "site" && parts[2] && /^\d{2}$/.test(parts[2])) {
    page(peopleShell(d, 0, false, peopleSearch, flashHere, parts[2]), keepScroll);
    drawPeopleGroups();
    return;
  }

  if (parts[1] === "add") {
    const f = addEdit?.route === key ? addEdit : null;
    page(addPersonForm(d, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
    return;
  }

  if (parts[1] === "tags") {
    const site = parts[2];
    const bucket = parts[3] as TagBucket | undefined;
    if (site && bucket && ["claimed", "unclaimed", "out"].includes(bucket) && d.siteNames[site]) {
      page(tagList(d, site, bucket), keepScroll);
      return;
    }
    if (site && !bucket && d.siteNames[site]) {
      page(siteTags(d, site), keepScroll);
      return;
    }
    page(tagsOverview(d), keepScroll);
    return;
  }

  if (parts[1] === "p" && parts[2]) {
    const qewId = decodeURIComponent(parts[2]);
    const p = d.people.find((x) => x.qewId === qewId);
    if (!p) {
      page(`<a class="back" href="#/people">People &amp; Badges</a>${errorScreen(`No person record for ${qewId}.`)}`);
      return;
    }
    const b = d.badges.find((x) => x["Badge ID"] === qewId);
    const admin = qewId.startsWith("ALL-");
    if (parts[3] === "edit" && !admin) {
      const f = personEdit?.route === key ? personEdit : null;
      page(personEditForm(p, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }
    if (parts[3] === "out" && !admin && b && !b.outOfServiceOn) {
      const f = outEdit?.route === key ? outEdit : null;
      page(outOfServiceForm(p, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }
    if (parts[3] === "loto" && !admin && lotoPending(p)) {
      const f = verifyEdit?.route === key ? verifyEdit : null;
      // Opened from the Training Portal trainee page: Close and Save go back there (Stage 4b).
      const back = parts[4] === "training" ? `training/${p.siteNumber}/${encodeURIComponent(qewId)}` : undefined;
      page(verifyLotoForm(p, d, today(), f?.entry ?? null, f?.busy ?? false, f?.error ?? "", back), keepScroll);
      return;
    }
    page(personDetail(p, b, d, allNodesDoneSet(d.records).has(qewId), today(), flashHere), keepScroll);
    return;
  }

  page(`<a class="back" href="#/people">People &amp; Badges</a>${errorScreen("That page was not found.")}`);
}

function qewNow(): string {
  return route()[1] === "p" && route()[2] ? decodeURIComponent(route()[2]!) : "";
}

/** Runs a People sheet's server call, then reloads and shows the line on the person page. */
async function confirmPeopleSheet(call: () => Promise<string>): Promise<void> {
  if (sheet?.kind !== "auth" && sheet?.kind !== "personActive") return;
  const sh = sheet;
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const line = await call();
    setSheet(null);
    setFlash(line);
    await refreshPeople();
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

function confirmAuthority(): Promise<void> {
  if (sheet?.kind !== "auth") return Promise.resolve();
  const { qewId, key, isOn } = sheet;
  return confirmPeopleSheet(async () => {
    const step = isAdminId(qewId) ? "setAdminAuthority" : "setAuthority";
    const res = await callServer<{ name: string; unchanged: boolean }>(step, { qewId, authority: key, isOn, confirmed: true });
    const label = authorityLabel(key);
    return res.unchanged ? `${label} was already ${isOn ? "on" : "off"}.` : `${label} ${isOn ? "on" : "off"} for ${res.name}. It takes effect at the next sign-in.`;
  });
}

function confirmPersonActive(): Promise<void> {
  if (sheet?.kind !== "personActive" || typeof peopleData !== "object" || !peopleData) return Promise.resolve();
  const { qewId } = sheet;
  const b = peopleData.badges.find((x) => x["Badge ID"] === qewId);
  const isActive = b?.Status !== "Active";
  return confirmPeopleSheet(async () => {
    const res = await callServer<{ name: string; unchanged: boolean }>("setPersonActive", { qewId, isActive, confirmed: true });
    return res.unchanged ? `${res.name} was already ${isActive ? "on" : "off"}.` : isActive ? `${res.name} is back on. Tag ${qewId} works again.` : `${res.name} is off. Tag ${qewId} no longer works.`;
  });
}

/** Shared shape for the four People forms: check on the page, call the server, go back with a line. */
async function submitPeopleForm<E>(
  setState: (s: { route: string; entry: E; busy: boolean; error: string } | null) => void,
  entry: E,
  problem: string | null,
  call: () => Promise<{ line: string; back: string }>,
): Promise<void> {
  const key = routeKey();
  if (problem) {
    setState({ route: key, entry, busy: false, error: problem });
    render(true);
    return;
  }
  setState({ route: key, entry, busy: true, error: "" });
  render(true);
  try {
    const { line, back } = await call();
    await refreshPeople();
    setState(null);
    setFlash(line, "green", back);
    goUpTo(back);
  } catch (e) {
    setState({ route: key, entry, busy: false, error: messageOf(e) });
    render(true);
  }
}

function formVal(f: HTMLFormElement, n: string): string {
  return ((f.elements.namedItem(n) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "").trim();
}

function submitPersonForm(f: HTMLFormElement): Promise<void> {
  const qewId = f.dataset.qew ?? "";
  const entry: PersonInput = {
    name: formVal(f, "name"),
    email: formVal(f, "email"),
    phone: formVal(f, "phone"),
    department: formVal(f, "department"),
    role: formVal(f, "role"),
    supervisor: formVal(f, "supervisor"),
    yearsExperience: formVal(f, "yearsExperience"),
    employeeId: formVal(f, "employeeId"),
    level: formVal(f, "level"),
  };
  const current = typeof peopleData === "object" ? peopleData?.people.find((p) => p.qewId === qewId) : undefined;
  return submitPeopleForm((s) => (personEdit = s), entry, personProblem(entry, current?.level ?? ""), async () => {
    const res = await callServer<{ name: string }>("savePerson", { qewId, ...entry });
    return { line: `${res.name} saved.`, back: `people/p/${encodeURIComponent(qewId)}` };
  });
}

function readAddForm(f: HTMLFormElement): NewPersonInput {
  return { siteNumber: formVal(f, "siteNumber"), name: formVal(f, "name"), email: formVal(f, "email"), level: formVal(f, "level") };
}

function submitAddForm(f: HTMLFormElement): Promise<void> {
  const entry = readAddForm(f);
  return submitPeopleForm((s) => (addEdit = s), entry, newPersonProblem(entry), async () => {
    const res = await callServer<{ qewId: string; name: string }>("addPerson", { ...entry });
    return { line: `${res.name} added as ${res.qewId}. Training and a verified LOTO date still apply.`, back: `people/p/${encodeURIComponent(res.qewId)}` };
  });
}

function submitOutForm(f: HTMLFormElement): Promise<void> {
  const qewId = f.dataset.qew ?? "";
  const entry = { reason: f.querySelector<HTMLInputElement>('input[name="reason"]:checked')?.value ?? "", note: formVal(f, "note") };
  return submitPeopleForm((s) => (outEdit = s), entry, outOfServiceProblem(entry.reason, entry.note), async () => {
    await callServer("takeTagOutOfService", { qewId, ...entry, confirmed: true });
    return { line: `Tag ${qewId} is out of service.`, back: `people/p/${encodeURIComponent(qewId)}` };
  });
}

function submitVerifyForm(f: HTMLFormElement): Promise<void> {
  const qewId = f.dataset.qew ?? "";
  const entry = formVal(f, "correctedDate");
  const p = typeof peopleData === "object" ? peopleData?.people.find((x) => x.qewId === qewId) : undefined;
  const problem = p && badge ? verifyProblem({ qewId, lotoTrainedOn: p.lotoTrainedOn ?? null, lotoVerifiedOn: p.lotoVerifiedOn ?? null }, badge.badgeId, entry, today()) : null;
  return submitPeopleForm((s) => (verifyEdit = s), entry, problem, async () => {
    const res = await callServer<{ name: string; lotoTrainedOn: string; corrected: boolean }>("verifyLotoDate", { qewId, correctedDate: entry });
    const [y, m, dd] = res.lotoTrainedOn.split("-");
    const back = f.dataset.back || `people/p/${encodeURIComponent(qewId)}`;
    // From the Training Portal: reload its data so the LOTO row shows Verified.
    if (back.startsWith("training/") && badge) data = await loadTraining(badge.company);
    return { line: `LOTO date ${m}/${dd}/${y} verified for ${res.name}${res.corrected ? " (corrected)" : ""}.`, back };
  });
}

// ---- Program Settings (PROGRAM_SETTINGS_WORDS.md, MOCKUP_PROGRAM_SETTINGS_APPROVED.html) ----

async function refreshSettings(): Promise<void> {
  if (!badge) return;
  settingsLoading = true;
  try {
    settingsData = await withTimeLimit(loadSettingsData(badge.company));
  } catch (e) {
    settingsData = `Could not read program settings: ${messageOf(e)}`;
  } finally {
    settingsLoading = false;
  }
}

function needSettings(keepScroll: boolean): SettingsData | null {
  if (typeof settingsData === "object" && settingsData) return settingsData;
  if (typeof settingsData === "string") {
    page(`${errorScreen(settingsData)}<button class="prim" type="button" data-act="settings-retry">Try Again</button>`);
    return null;
  }
  if (!settingsLoading) {
    void refreshSettings().then(() => {
      if (route()[0] === "settings") render(keepScroll);
    });
  }
  page(loadingScreen("program settings"));
  return null;
}

function renderSettings(keepScroll: boolean): void {
  const parts = route();
  // Google Drive (SITE_FOLDERS_WORDS.md rev 3.1, mockup rev 2 panel D). The sign-in returns here with ?drive=.
  // It reads its own record, so it opens without waiting on the rest of Program Settings (2026-10-22).
  if (parts[1]?.startsWith("drive")) {
    if (parts[1].includes("?")) return takeConnectResult(parts[1]);
    const b = badge;
    if (!b?.sites.includes("ALL")) {
      page(`<a class="back" href="#/settings">Program Settings</a>${errorScreen("Google Drive is set up by an admin badge that covers all sites.")}`);
      return;
    }
    page(driveSettingsScreen(b.company, () => render(true)), keepScroll);
    return;
  }
  // Printer Setup (list 2, 2026-10-25): kept on this iPad, so it needs nothing from the cloud.
  if (parts[1] === "printer") {
    page(printerSetupScreen(), keepScroll);
    return;
  }
  const d = needSettings(keepScroll);
  if (!d) return;
  const key = parts.join("/");
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  switch (parts[1]) {
    case undefined:
      page(settingsMenu(d, flashHere), keepScroll);
      return;
    case "program": {
      const f = programEdit?.route === key ? programEdit : null;
      page(programForm(d.settings, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }
    case "contacts": {
      if (!parts[2]) {
        page(programContactsList(d.programContacts, flashHere), keepScroll);
        return;
      }
      const c = parts[2] === "new" ? null : (d.programContacts.find((x) => x.contactId === parts[2]) ?? undefined);
      if (c === undefined) {
        page(`<a class="back" href="#/settings/contacts">Program Contacts</a>${errorScreen("That contact was not found.")}`);
        return;
      }
      const f = pcEdit?.route === key ? pcEdit : null;
      page(programContactForm(c, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }
    case "eewp":
      page(eewpPage(d, flashHere), keepScroll);
      return;
    case "loto":
      page(lotoPage(d, flashHere), keepScroll);
      return;
    case "limits": {
      const def = parts[2] ? LIMITS.find((l) => l.field === parts[2]) : undefined;
      if (def) {
        const f = limitEdit?.route === key ? limitEdit : null;
        page(limitForm(def, d.settings, f?.entry ?? null, f?.error ?? ""), keepScroll);
        return;
      }
      page(limitsPage(d.settings, flashHere), keepScroll);
      return;
    }
    default:
      page(`<a class="back" href="#/settings">Program Settings</a>${errorScreen("That page was not found.")}`);
  }
}

async function confirmSettingSheet(call: () => Promise<{ line: string; back?: string }>): Promise<void> {
  if (sheet?.kind !== "settingSwitch" && sheet?.kind !== "limit") return;
  const sh = sheet;
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const { line, back } = await call();
    setSheet(null);
    await refreshSettings();
    if (back) {
      limitEdit = null;
      setFlash(line, "green", back);
      goUpTo(back);
      return;
    }
    setFlash(line);
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

function confirmSettingSwitch(): Promise<void> {
  if (sheet?.kind !== "settingSwitch") return Promise.resolve();
  const { field, isOn } = sheet;
  const label = ALL_SWITCHES.find((x) => x.field === field)?.label ?? field;
  return confirmSettingSheet(async () => {
    const res = await callServer<{ unchanged: boolean }>("setProgramSwitch", { field, isOn, confirmed: true });
    return { line: res.unchanged ? `${label} was already ${isOn ? "on" : "off"}.` : `${label} turned ${isOn ? "on" : "off"}.` };
  });
}

function confirmLimit(): Promise<void> {
  if (sheet?.kind !== "limit") return Promise.resolve();
  const { field, to } = sheet;
  const def = LIMITS.find((x) => x.field === field)!;
  return confirmSettingSheet(async () => {
    const res = await callServer<{ unchanged: boolean }>("setSafetyLimit", { field, value: to, confirmed: true });
    const v = `${to.toLocaleString("en-US")} ${def.unit}`;
    return { line: res.unchanged ? `${def.label} was already ${v}.` : `${def.label} changed to ${v}.`, back: "settings/limits" };
  });
}

function submitLimitForm(f: HTMLFormElement): void {
  const key = routeKey();
  const field = f.dataset.field as LimitField;
  const entry = formVal(f, "value");
  const problem = limitProblem(field, entry);
  if (problem) {
    limitEdit = { route: key, entry, error: problem };
    render(true);
    return;
  }
  const d = typeof settingsData === "object" ? settingsData : null;
  if (!d) return;
  limitEdit = { route: key, entry, error: "" };
  setSheet({ kind: "limit", field, from: d.settings[field], to: limitValue(entry), busy: false, error: "" });
  render(true);
}

async function submitProgramForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const entry: ProgramEntry = { orgName: formVal(f, "orgName"), programContactEmail: formVal(f, "programContactEmail"), companyEmailDomains: formVal(f, "companyEmailDomains") };
  const problem = programInfoProblem(entry.orgName, entry.programContactEmail) ?? domainsProblem(parseDomains(entry.companyEmailDomains));
  if (problem) {
    programEdit = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  programEdit = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    await callServer("saveProgramInfo", { ...entry });
    await refreshSettings();
    programEdit = null;
    setFlash("Program saved. The next scan shows it.", "green", "settings");
    goUpTo("settings");
  } catch (e) {
    programEdit = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

async function submitProgramContactForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const contactId = f.dataset.contact ?? "";
  const on = (n: string) => Boolean((f.elements.namedItem(n) as HTMLInputElement | null)?.checked);
  const usedFor = { ...NO_PROGRAM_TICKS };
  for (const u of PROGRAM_USED_FOR) usedFor[u.key] = on(u.key);
  const entry: ProgramContactInput = { name: formVal(f, "name"), email: formVal(f, "email"), isActive: contactId ? on("isActive") : true, usedFor };
  const problem = programContactProblem(entry);
  if (problem) {
    pcEdit = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  pcEdit = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ contactId: string; name: string; added: boolean }>("saveProgramContact", { ...(contactId ? { contactId } : {}), ...entry });
    await refreshSettings();
    pcEdit = null;
    setFlash(res.added ? `${res.name} added as ${res.contactId}.` : `${res.name} saved.`, "green", "settings/contacts");
    goUpTo("settings/contacts");
  } catch (e) {
    pcEdit = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

// ---- Lists (LISTS_PAGE_WORDS.md, MOCKUP_LISTS_APPROVED.html) ----

async function refreshLists(): Promise<void> {
  if (!badge) return;
  listsLoading = true;
  try {
    listsData = await loadListsData(badge.company);
  } catch (e) {
    listsData = `Could not read the lists: ${messageOf(e)}`;
  } finally {
    listsLoading = false;
  }
}

function needLists(keepScroll: boolean): ListsData | null {
  if (typeof listsData === "object" && listsData) return listsData;
  if (typeof listsData === "string") {
    page(`${errorScreen(listsData)}<button class="prim" type="button" data-act="lists-retry">Try Again</button>`);
    return null;
  }
  if (!listsLoading) {
    void refreshLists().then(() => {
      if (route()[0] === "lists") render(keepScroll);
    });
  }
  page(loadingScreen("lists"));
  return null;
}

function renderLists(keepScroll: boolean): void {
  const d = needLists(keepScroll);
  if (!d) return;
  const parts = route();
  const key = parts.join("/");
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  const notFound = (back: string, label: string) => page(`<a class="back" href="#/${back}">${label}</a>${errorScreen("That item was not found.")}`);

  if (!parts[1]) {
    page(listsMenu(d), keepScroll);
    return;
  }
  if (parts[1] === "classes") {
    if (!parts[2]) {
      page(classList(d, flashHere, listOrder?.route === key ? listOrder.view : undefined), keepScroll);
      return;
    }
    const code = decodeURIComponent(parts[2]);
    const c = d.classes.find((x) => x.code === code);
    if (!c) return notFound("lists/classes", "Equipment Classes");
    if (parts[3] === "edit") {
      const f = classEdit?.route === key ? classEdit : null;
      page(classForm(c, f?.entry ?? null, f?.error ?? ""), keepScroll);
      return;
    }
    page(classDetail(c, sortClasses(d.classes).map((x) => x.code), flashHere), keepScroll);
    return;
  }
  if (parts[1] === "fields") {
    if (!parts[2]) {
      page(fieldListsIndex(d), keepScroll);
      return;
    }
    const listName = listNameFromSlug(d, parts[2]);
    if (!listName) return notFound("lists/fields", "Field Lists");
    if (parts[3] === "new" || (parts[3] === "v" && parts[4])) {
      const v = parts[3] === "new" ? null : (d.values.find((x) => x.id === decodeURIComponent(parts[4]!)) ?? undefined);
      if (v === undefined) return notFound(`lists/fields/${parts[2]}`, listName);
      const f = valueEdit?.route === key ? valueEdit : null;
      page(fieldValueForm(d, listName, v, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }
    page(fieldList(d, listName, flashHere, listOrder?.route === key ? listOrder.view : undefined), keepScroll);
    return;
  }
  if (parts[1] === "plants") {
    if (!parts[2]) {
      page(plantList(d, flashHere, liveSiteNumbers() ?? []), keepScroll);
      return;
    }
    const p = parts[2] === "new" ? null : (d.plants.find((x) => x.siteNumber === parts[2]) ?? undefined);
    if (p === undefined) return notFound("lists/plants", "Plants");
    const f = plantEdit?.route === key ? plantEdit : null;
    page(plantForm(p, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
    return;
  }
  notFound("lists", "Lists");
}

async function confirmListsSheet(call: () => Promise<{ line: string; back?: string }>): Promise<void> {
  if (sheet?.kind !== "classSave" && sheet?.kind !== "itemActive" && sheet?.kind !== "plantDelete") return;
  const sh = sheet;
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const { line, back } = await call();
    setSheet(null);
    await refreshLists();
    if (back) {
      classEdit = null;
      plantEdit = null;
      setFlash(line, "green", back);
      goUpTo(back);
      return;
    }
    setFlash(line);
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

function confirmClassSave(): Promise<void> {
  if (sheet?.kind !== "classSave") return Promise.resolve();
  const { code, entry } = sheet;
  return confirmListsSheet(async () => {
    const res = await callServer<{ name: string }>("saveEquipmentClass", { code, ...entry, confirmed: true });
    return { line: `${res.name} saved.`, back: `lists/classes/${encodeURIComponent(code)}` };
  });
}

function confirmItemActive(): Promise<void> {
  if (sheet?.kind !== "itemActive") return Promise.resolve();
  const { itemKind, id, isOn } = sheet;
  return confirmListsSheet(async () => {
    const res = await callServer<{ name: string; unchanged: boolean }>("setListItemActive", { kind: itemKind, id, isActive: isOn, confirmed: true });
    return { line: res.unchanged ? `${res.name} was already ${isOn ? "on" : "off"}.` : `${res.name} turned ${isOn ? "on" : "off"}.` };
  });
}

function confirmPlantDelete(): Promise<void> {
  if (sheet?.kind !== "plantDelete") return Promise.resolve();
  const { siteNumber } = sheet;
  return confirmListsSheet(async () => {
    const res = await callServer<{ name: string }>("deletePlant", { siteNumber, confirmed: true });
    return { line: `${res.name} deleted.`, back: "lists/plants" };
  });
}

async function moveItem(el: HTMLElement): Promise<void> {
  const kind = el.dataset.kind;
  const id = el.dataset.id ?? "";
  const direction = el.dataset.dir;
  (el as HTMLButtonElement).disabled = true;
  try {
    await callServer("moveListItem", { kind, id, direction });
    await refreshLists();
  } catch (e) {
    setFlash(messageOf(e), "red");
  }
  render(true);
}

function readClassForm(f: HTMLFormElement): ClassInput {
  return {
    name: formVal(f, "name"),
    alsoAccepts: formVal(f, "alsoAccepts"),
    nec11016Label: Boolean((f.elements.namedItem("nec11016Label") as HTMLInputElement | null)?.checked),
    ieeeClass: formVal(f, "ieeeClass"),
    card: formVal(f, "card"),
    devices: formVal(f, "devices"),
    rating: formVal(f, "rating"),
    positionType: formVal(f, "positionType"),
    notes: formVal(f, "notes"),
  };
}

function submitClassForm(f: HTMLFormElement): void {
  const key = routeKey();
  const code = f.dataset.code ?? "";
  const d = typeof listsData === "object" ? listsData : null;
  const c = d?.classes.find((x) => x.code === code);
  if (!d || !c) return;
  const entry = readClassForm(f);
  const problem = classProblem(entry, d.classes, code);
  const changes = problem ? [] : classChanges(c, entry);
  if (problem || !changes.length) {
    classEdit = { route: key, entry, error: problem ?? "Nothing changed. Change a field, or Cancel." };
    render(true);
    return;
  }
  classEdit = { route: key, entry, error: "" };
  setSheet({ kind: "classSave", code, entry, changes, busy: false, error: "" });
  render(true);
}

async function submitValueForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const listName = f.dataset.list ?? "";
  const id = f.dataset.id ?? "";
  const entry: FieldValueInput = {
    value: formVal(f, "value"),
    group: formVal(f, "group"),
    notes: formVal(f, "notes"),
    isActive: Boolean((f.elements.namedItem("isActive") as HTMLInputElement | null)?.checked),
  };
  const d = typeof listsData === "object" ? listsData : null;
  const problem = d ? fieldValueProblem(entry, d.values, listName, id) : null;
  if (problem) {
    valueEdit = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  valueEdit = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ value: string; added: boolean }>("saveFieldValue", { listName, ...(id ? { id } : {}), ...entry });
    await refreshLists();
    valueEdit = null;
    const back = `lists/fields/${listSlug(listName)}`;
    setFlash(res.added ? `${res.value} added to ${listName}.` : `${res.value} saved.`, "green", back);
    goUpTo(back);
  } catch (e) {
    valueEdit = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

async function submitPlantForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const oldNumber = f.dataset.old ?? "";
  const entry: PlantInput = { siteNumber: formVal(f, "siteNumber"), name: formVal(f, "name"), city: formVal(f, "city"), state: formVal(f, "state").toUpperCase() };
  const d = typeof listsData === "object" ? listsData : null;
  const problem = d ? plantProblem(entry, d.plants, oldNumber, liveSiteNumbers() ?? undefined) : null;
  if (problem) {
    plantEdit = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  plantEdit = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ siteNumber: string; name: string; added: boolean }>("savePlant", { ...(oldNumber ? { oldNumber } : {}), ...entry });
    await refreshLists();
    plantEdit = null;
    setFlash(res.added ? `${res.name} added as plant ${res.siteNumber}.` : `${res.name} saved.`, "green", "lists/plants");
    goUpTo("lists/plants");
  } catch (e) {
    plantEdit = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

// ---- Tasks (TASKS_PAGE_WORDS.md, MOCKUP_TASKS_APPROVED.html) ----

async function refreshTasks(): Promise<void> {
  if (!badge) return;
  tasksLoading = true;
  try {
    tasksData = await loadTasksData(badge.company);
  } catch (e) {
    tasksData = `Could not read the tasks: ${messageOf(e)}`;
  } finally {
    tasksLoading = false;
  }
}

function needTasks(keepScroll: boolean, from = "tasks"): TasksData | null {
  if (typeof tasksData === "object" && tasksData) return tasksData;
  if (typeof tasksData === "string") {
    page(`${errorScreen(tasksData)}<button class="prim" type="button" data-act="tasks-retry">Try Again</button>`);
    return null;
  }
  if (!tasksLoading) {
    void refreshTasks().then(() => {
      if (route()[0] === from) render(keepScroll);
    });
  }
  page(loadingScreen(from === "tasks" ? "tasks" : "the SOP Library"));
  return null;
}

function drawTaskGroups(): void {
  const host = root.querySelector<HTMLElement>("#task-groups");
  const d = typeof tasksData === "object" ? tasksData : null;
  if (host && d) host.innerHTML = groupsFor(d, taskSearch);
}

function dropTaskSop(): void {
  if (taskSop?.frameUrl) URL.revokeObjectURL(taskSop.frameUrl);
  taskSop = null;
}

async function openTaskSop(taskId: string, fileUrl: string): Promise<void> {
  dropTaskSop();
  taskSop = { taskId, frameUrl: null, error: "" };
  try {
    const r = await fetch(fileUrl);
    if (!r.ok) throw new Error("The SOP page did not load.");
    const html = await r.text();
    if (taskSop?.taskId !== taskId) return;
    taskSop = { taskId, frameUrl: URL.createObjectURL(new Blob([reviewHtml(html)], { type: "text/html" })), error: "" };
  } catch (e) {
    if (taskSop?.taskId === taskId) taskSop = { taskId, frameUrl: null, error: `Could not open the SOP. ${messageOf(e)}` };
  }
  if (route()[0] === "tasks" && route()[3] === "sop") render(true);
}

function renderTasks(keepScroll: boolean): void {
  const d = needTasks(keepScroll);
  if (!d) return;
  const parts = route();
  const key = parts.join("/");
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  const notFound = () => page(`<a class="back" href="#/tasks">Tasks</a>${errorScreen("That task was not found.")}`);

  if (!parts[1]) {
    page(tasksShell(d, taskSearch, flashHere), keepScroll);
    drawTaskGroups();
    return;
  }
  if (parts[1] === "new") {
    const f = taskAdd?.route === key ? taskAdd : null;
    page(addTaskForm(d, f?.entry ?? EMPTY_TASK, f?.busy ?? false, f?.error ?? ""), keepScroll);
    return;
  }
  if (parts[1] === "t" && parts[2]) {
    const id = decodeURIComponent(parts[2]);
    const t = d.tasks.find((x) => x.taskId === id);
    if (!t) return notFound();
    if (parts[3] === "sop") {
      const sop = d.sops.find((s) => s.sopNumber === t.sopNumber);
      if (!sop || !sop.fileUrl) {
        page(`<a class="back" href="#/tasks/t/${encodeURIComponent(id)}">${t.name}</a>${errorScreen(`${t.sopNumber || "This task's SOP"} is not in the SOP Library yet.`)}`);
        return;
      }
      if (taskSop?.taskId !== id) void openTaskSop(id, sop.fileUrl);
      page(taskSopView(t, sop.title, taskSop?.frameUrl ?? null, taskSop?.error ?? ""), keepScroll);
      return;
    }
    page(taskDetail(t, d, flashHere), keepScroll);
    return;
  }
  notFound();
}

async function confirmTaskSheet(call: () => Promise<string>): Promise<void> {
  if (sheet?.kind !== "taskSwitch" && sheet?.kind !== "taskChange") return;
  const sh = sheet;
  sh.reason = (root.querySelector<HTMLInputElement>("#task-reason")?.value ?? sh.reason).trim();
  const problem = reasonProblem(sh.reason);
  if (problem) {
    sh.error = problem;
    render(true);
    return;
  }
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const line = await call();
    setSheet(null);
    await refreshTasks();
    setFlash(line);
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

function confirmTaskSwitch(): Promise<void> {
  if (sheet?.kind !== "taskSwitch") return Promise.resolve();
  const sh = sheet;
  return confirmTaskSheet(async () => {
    const res = await callServer<{ name: string; unchanged: boolean }>("setTaskActive", { taskId: sh.taskId, isActive: sh.isOn, reason: sh.reason, confirmed: true });
    return res.unchanged ? `${res.name} was already ${sh.isOn ? "on" : "off"}.` : `${res.name} turned ${sh.isOn ? "on" : "off"}.`;
  });
}

function confirmTaskChange(): Promise<void> {
  if (sheet?.kind !== "taskChange") return Promise.resolve();
  const sh = sheet;
  const d = typeof tasksData === "object" ? tasksData : null;
  const t = d?.tasks.find((x) => x.taskId === sh.taskId);
  const to = sh.field === "tier" ? Number(sh.to) : sh.to;
  const problem = t ? changeProblem(t, sh.field, to, (root.querySelector<HTMLInputElement>("#task-reason")?.value ?? sh.reason)) : "That task was not found.";
  if (problem) {
    sh.error = problem;
    render(true);
    return Promise.resolve();
  }
  return confirmTaskSheet(async () => {
    const res = await callServer<{ name: string }>("changeTask", { taskId: sh.taskId, field: sh.field, to, reason: sh.reason, confirmed: true });
    return sh.field === "tier" ? `${res.name} is now Tier ${to}.` : `${res.name} is now ${to}.`;
  });
}

/** The Turn On / Change button: on only when a reason is typed (and, for a change, a new value picked). */
function syncTaskSheetButton(): void {
  if (sheet?.kind !== "taskSwitch" && sheet?.kind !== "taskChange") return;
  const btn = root.querySelector<HTMLButtonElement>("[data-needs-reason]");
  if (!btn || sheet.busy) return;
  let picked = true;
  if (sheet.kind === "taskChange") {
    const sh = sheet;
    const t = typeof tasksData === "object" ? tasksData?.tasks.find((x) => x.taskId === sh.taskId) : undefined;
    const current = t ? (sh.field === "tier" ? String(t.tier) : t.taskType) : "";
    picked = Boolean(sh.to) && sh.to !== current;
    const line = root.querySelector<HTMLElement>("#task-change-line");
    if (line && t && picked) line.textContent = changeLine(t, sh.field, sh.field === "tier" ? Number(sh.to) : sh.to);
  }
  btn.disabled = !(picked && sheet.reason.trim());
}

function readTaskForm(f: HTMLFormElement): NewTaskInput {
  const tier = f.querySelector<HTMLInputElement>('input[name="tier"]:checked')?.value;
  const taskType = f.querySelector<HTMLInputElement>('input[name="taskType"]:checked')?.value ?? "";
  return { name: formVal(f, "name"), tier: tier ? Number(tier) : null, taskType: isTaskType(taskType) ? taskType : "", sopNumber: formVal(f, "sopNumber").toUpperCase() };
}

function syncTaskForm(f: HTMLFormElement): void {
  const entry = readTaskForm(f);
  const ready = canSaveNewTask(entry);
  const btn = root.querySelector<HTMLButtonElement>("#task-save");
  if (btn && !taskAdd?.busy) btn.disabled = !ready;
  const note = root.querySelector<HTMLElement>("#task-form-note");
  if (note) note.textContent = ready ? "Saves switched Off. Turn it on when its SOP page is ready." : "Tier and Task Type have no default. Save stays off until name, tier and Task Type are all filled in.";
}

async function submitTaskForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const entry = readTaskForm(f);
  const d = typeof tasksData === "object" ? tasksData : null;
  const problem = d ? newTaskProblem(entry, d.tasks) : null;
  if (problem) {
    taskAdd = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  taskAdd = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ taskId: string; name: string }>("addTask", { ...entry });
    await refreshTasks();
    taskAdd = null;
    const back = `tasks/t/${encodeURIComponent(res.taskId)}`;
    setFlash(`${res.taskId} ${res.name} added. It is Off until you turn it on.`, "green", back);
    goUpTo(back);
  } catch (e) {
    taskAdd = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

// ---- Reference Library (REFERENCE_LIBRARY_WORDS.md, MOCKUP_REFERENCE_LIBRARY_APPROVED.html) ----

async function refreshRefs(): Promise<void> {
  if (!badge) return;
  refsLoading = true;
  try {
    refsData = await loadReferencesData(badge.company);
  } catch (e) {
    refsData = `Could not read the Reference Library: ${messageOf(e)}`;
  } finally {
    refsLoading = false;
  }
}

function needRefs(keepScroll: boolean): ReferencesData | null {
  if (typeof refsData === "object" && refsData) return refsData;
  if (typeof refsData === "string") {
    page(`${errorScreen(refsData)}<button class="prim" type="button" data-act="refs-retry">Try Again</button>`);
    return null;
  }
  if (!refsLoading) {
    void refreshRefs().then(() => {
      if (route()[0] === "references") render(keepScroll);
    });
  }
  page(loadingScreen("the Reference Library"));
  return null;
}

function drawRefGroups(): void {
  const host = root.querySelector<HTMLElement>("#ref-groups");
  const d = typeof refsData === "object" ? refsData : null;
  if (host && d) host.innerHTML = refGroupsHtml(d, refSearch);
}

function fileInfo(f: File | null): FileInfo | null {
  return f ? { name: f.name, size: f.size, type: f.type } : null;
}

/** The form a route opens, with what a new edition or Replace File starts from. */
function refFormStart(mode: FormMode, base: { kind: string; number: string; title: string; covers: string } | null): RefInput {
  if (mode === "edition" && base) return { ...EMPTY_REF, kind: base.kind, number: base.number, title: base.title, covers: base.covers };
  if (mode === "replace" && base) return { ...EMPTY_REF, kind: base.kind, title: base.title, covers: base.covers };
  return { ...EMPTY_REF };
}

function renderReferences(keepScroll: boolean): void {
  const d = needRefs(keepScroll);
  if (!d) return;
  const parts = route();
  const key = parts.join("/");
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  const notFound = () => page(`<a class="back" href="#/references">Reference Library</a>${errorScreen("That reference was not found.")}`);

  if (!parts[1]) {
    page(refsShell(d, refSearch, flashHere), keepScroll);
    drawRefGroups();
    return;
  }
  if (parts[1] === "new") {
    if (refAdd?.route !== key) refAdd = { route: key, entry: refFormStart("new", null), file: null, busy: "", error: "" };
    page(refForm("new", d, null, refAdd.entry, fileInfo(refAdd.file), refAdd.busy, refAdd.error), keepScroll);
    return;
  }
  if (parts[1] === "r" && parts[2]) {
    const id = decodeURIComponent(parts[2]);
    const r = d.refs.find((x) => x.refId === id);
    if (!r) return notFound();
    if (parts[3] === "edition" || parts[3] === "replace") {
      const mode = parts[3] as FormMode;
      if (r.status !== "Current") {
        page(`<a class="back" href="#/references/r/${parts[2]}">${r.refId}</a>${errorScreen(`${r.refId} is superseded. Use the current edition.`)}`);
        return;
      }
      if (refAdd?.route !== key) refAdd = { route: key, entry: refFormStart(mode, r), file: null, busy: "", error: "" };
      page(refForm(mode, d, r, refAdd.entry, fileInfo(refAdd.file), refAdd.busy, refAdd.error), keepScroll);
      return;
    }
    if (parts[3] === "edit") {
      const f = refEdit?.route === key ? refEdit : null;
      page(refEditForm(r, f?.entry ?? null, f?.busy ?? false, f?.error ?? ""), keepScroll);
      return;
    }
    page(refDetail(r, d, flashHere), keepScroll);
    return;
  }
  notFound();
}

function readRefForm(f: HTMLFormElement): RefInput {
  const kind = f.querySelector<HTMLInputElement>('input[name="kind"]:checked')?.value ?? refAdd?.entry.kind ?? "";
  const val = (n: string) => (f.elements.namedItem(n) as HTMLInputElement | null)?.value ?? refAdd?.entry[n as keyof RefInput] ?? "";
  return { kind: isKind(kind) ? kind : "", title: val("title"), number: val("number"), edition: val("edition"), covers: val("covers"), notes: val("notes") };
}

/** Keeps what is typed, and the Save button and note in step with it, without a re-draw. */
function syncRefForm(f: HTMLFormElement): void {
  if (!refAdd) return;
  refAdd.entry = readRefForm(f);
  const mode = (f.dataset.mode as FormMode) ?? "new";
  const ready = refFormReady(mode, refAdd.entry, fileInfo(refAdd.file)) && !refAdd.busy;
  const btn = root.querySelector<HTMLButtonElement>("#ref-save");
  if (btn) btn.disabled = !ready;
  const note = root.querySelector<HTMLElement>("#ref-form-note");
  if (note) {
    note.textContent = formNote(mode, refAdd.entry, fileInfo(refAdd.file), ready);
    note.classList.toggle("green", ready);
  }
}

/** Uploads the chosen file, then files it with saveReference. */
async function uploadAndSave(mode: FormMode, refId: string, setBusy: (s: string) => void): Promise<{ refId: string; superseded?: string }> {
  if (!badge || !refAdd?.file) throw new Error("Choose the file.");
  setBusy("Uploading…");
  const uploadName = await uploadReferenceFile(badge.company, refAdd.file);
  setBusy("Filing…");
  const e = refAdd.entry;
  return callServer<{ refId: string; superseded?: string }>("saveReference", {
    mode,
    uploadName,
    fileName: refAdd.file.name,
    ...(mode === "replace" ? { refId, covers: e.covers } : { ...e, ...(mode === "edition" ? { oldRefId: refId } : {}) }),
  });
}

async function submitRefForm(f: HTMLFormElement): Promise<void> {
  const d = typeof refsData === "object" ? refsData : null;
  if (!d || !refAdd) return;
  const mode = (f.dataset.mode as FormMode) ?? "new";
  const refId = f.dataset.ref ?? "";
  refAdd.entry = readRefForm(f);
  const file = fileInfo(refAdd.file);
  const old = d.refs.find((x) => x.refId === refId);
  const problem =
    mode === "replace"
      ? refAdd.file
        ? null
        : "Choose the new file."
      : mode === "edition" && old
        ? newEditionProblem(refAdd.entry, file, old, d.refs)
        : newRefProblem(refAdd.entry, file, d.refs);
  if (problem) {
    refAdd.error = problem;
    render(true);
    return;
  }
  if (mode === "edition") {
    refAdd.error = "";
    setSheet({ kind: "refEdition", refId, busy: "", error: "" });
    render(true);
    return;
  }
  const st = refAdd;
  st.error = "";
  try {
    const res = await uploadAndSave(mode, refId, (b) => {
      st.busy = b;
      render(true);
    });
    await refreshRefs();
    refAdd = null;
    const back = `references/r/${encodeURIComponent(res.refId)}`;
    setFlash(mode === "replace" ? `File replaced on ${res.refId}. The old file stays in History.` : `${res.refId} added. The drafter can read it after the next copy for Claude.`, "green", back);
    goUpTo(back);
  } catch (e) {
    st.busy = "";
    st.error = messageOf(e);
    render(true);
  }
}

async function confirmRefEdition(): Promise<void> {
  if (sheet?.kind !== "refEdition" || !refAdd) return;
  const sh = sheet;
  sh.error = "";
  try {
    const res = await uploadAndSave("edition", sh.refId, (b) => {
      sh.busy = b;
      render(true);
    });
    setSheet(null);
    await refreshRefs();
    refAdd = null;
    const back = `references/r/${encodeURIComponent(res.refId)}`;
    setFlash(`${res.refId} is now current. ${res.superseded ?? sh.refId} turned Superseded and keeps its file.`, "green", back);
    goUpTo(back);
  } catch (e) {
    sh.busy = "";
    sh.error = messageOf(e);
    render(true);
  }
}

async function confirmRefRetire(): Promise<void> {
  if (sheet?.kind !== "refRetire") return;
  const sh = sheet;
  sh.reason = (root.querySelector<HTMLInputElement>("#ref-reason")?.value ?? sh.reason).trim();
  const problem = refReasonProblem(sh.reason);
  if (problem) {
    sh.error = problem;
    render(true);
    return;
  }
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const res = await callServer<{ name: string; unchanged: boolean }>("retireReference", { refId: sh.refId, reason: sh.reason, confirmed: true });
    setSheet(null);
    await refreshRefs();
    setFlash(res.unchanged ? `${res.name} was already superseded.` : `${res.name} retired. It stays in the library with its file.`);
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

async function submitRefEdit(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const refId = f.dataset.ref ?? "";
  const entry = { title: formVal(f, "title"), covers: formVal(f, "covers"), notes: formVal(f, "notes") };
  const d = typeof refsData === "object" ? refsData : null;
  const r = d?.refs.find((x) => x.refId === refId);
  const problem = r ? refFieldsProblem({ kind: r.kind, number: r.number, edition: r.edition, ...entry }) : "That reference was not found.";
  if (problem) {
    refEdit = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  refEdit = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ name: string; unchanged: boolean }>("changeReference", { refId, ...entry });
    await refreshRefs();
    refEdit = null;
    const back = `references/r/${encodeURIComponent(refId)}`;
    setFlash(res.unchanged ? "Nothing changed." : `${res.name} saved.`, "green", back);
    goUpTo(back);
  } catch (e) {
    refEdit = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

/** Opens a library file in a new tab. The tab opens on the tap, so no pop-up blocker stops it. */
async function openRefFile(path: string): Promise<void> {
  if (!path) return;
  const w = window.open("", "_blank");
  try {
    const url = await referenceFileUrl(path);
    if (w) w.location.href = url;
    else location.href = url;
  } catch (e) {
    w?.close();
    setFlash(`Could not open the file. ${messageOf(e)}`, "red");
    render(true);
  }
}

// ---- Email Log (EMAIL_SENDER_WORDS.md, MOCKUP_EMAIL_SENDER.html, 2026-10-10) ----

async function refreshEmails(): Promise<void> {
  if (!badge) return;
  emailLoading = true;
  try {
    emailData = await loadEmailData(badge.company);
  } catch (e) {
    emailData = `Could not read the Email Log: ${messageOf(e)}`;
  } finally {
    emailLoading = false;
  }
}

function needEmails(keepScroll: boolean): EmailData | null {
  if (typeof emailData === "object" && emailData) return emailData;
  if (typeof emailData === "string") {
    page(`${errorScreen(emailData)}<button class="prim" type="button" data-act="em-retry">Try Again</button>`);
    return null;
  }
  if (!emailLoading) {
    void refreshEmails().then(() => {
      if (route()[0] === "email-log") render(keepScroll);
    });
  }
  page(loadingScreen("the Email Log"));
  return null;
}

/** #/email-log/{site}, #/email-log/{site}/failed, #/email-log/m/{emailId}. */
function renderEmailLog(keepScroll: boolean): void {
  const d = needEmails(keepScroll);
  if (!d) return;
  const parts = route();
  if (parts[1] === "m" && parts[2]) {
    const id = decodeURIComponent(parts[2]);
    const r = d.rows.find((x) => x.emailId === id);
    if (!r) {
      page(`<a class="back" href="${emailListHref}">Email Log</a>${errorScreen("That email is not in the newest emails read.")}`);
      return;
    }
    const siteName = r.siteNumber ? d.siteNames[r.siteNumber] ?? `Site ${r.siteNumber}` : "Program";
    page(emailRecord(r, siteName, emailListHref), keepScroll);
    return;
  }
  const site = parts[1] && (parts[1] === "all" || d.siteNames[parts[1]]) ? parts[1] : "all";
  const failedOnly = parts[2] === "failed";
  emailListHref = `#/email-log/${site}${failedOnly ? "/failed" : ""}`;
  page(emailLog(d.rows, d.siteNames, site, failedOnly, emailTest), keepScroll);
}

/** Send Test Email: two emails, one through each route. The log reads again after the sender has had a moment. */
async function sendTestEmails(): Promise<void> {
  emailTest = { busy: true, error: "", note: "" };
  render(true);
  try {
    await callServer<{ emailIds: string[] }>("sendTestEmail", {});
    // Give the sender a moment, then read the log again. Test emails are program-wide, so they list under All Sites.
    await new Promise((ok) => setTimeout(ok, 8000));
    await refreshEmails();
    emailTest = { busy: false, error: "", note: "Two test emails queued. They are at the top of All Sites. A row still saying Sending is done in a minute; open the page again." };
    if (route()[0] === "email-log" && route()[1] !== "all") {
      replacingHash = true;
      location.replace("#/email-log/all");
      return;
    }
  } catch (e) {
    emailTest = { busy: false, error: messageOf(e), note: "" };
  }
  if (route()[0] === "email-log") render(true);
}

// ---- Work Requests (WORK_REQUEST_WORDS.md, MOCKUP_WORK_REQUESTS.html, 2026-10-11) ----

async function refreshWorkRequests(): Promise<void> {
  if (!badge) return;
  wrLoading = true;
  try {
    wrData = await loadWorkRequests(badge.company);
  } catch (e) {
    wrData = `Could not read the work requests: ${messageOf(e)}`;
  } finally {
    wrLoading = false;
  }
}

function needWorkRequests(keepScroll: boolean): WorkRequestData | null {
  if (typeof wrData === "object" && wrData) return wrData;
  if (typeof wrData === "string") {
    page(`${errorScreen(wrData)}<button class="prim" type="button" data-act="wr-retry">Try Again</button>`);
    return null;
  }
  if (!wrLoading) {
    void refreshWorkRequests().then(() => {
      if (route()[0] === "work-requests") render(keepScroll);
    });
  }
  page(loadingScreen("the work requests"));
  return null;
}

/** #/work-requests/{site}, #/work-requests/{site}/all, #/work-requests/r/{requestId}. */
function renderWorkRequests(keepScroll: boolean): void {
  const d = needWorkRequests(keepScroll);
  if (!d) return;
  const parts = route();
  const key = routeKey();
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  if (parts[1] === "r" && parts[2]) {
    const id = decodeURIComponent(parts[2]);
    const r = d.rows.find((x) => x.requestId === id);
    if (!r) {
      page(`<a class="back" href="${wrListHref}">Work Requests</a>${errorScreen("That work request is not on record.")}`);
      return;
    }
    page(workRequestRecord(r, d.siteNames[r.siteNumber] ?? `Site ${r.siteNumber}`, wrListHref, wrScreen, flashHere), keepScroll);
    return;
  }
  const site = parts[1] && (parts[1] === "all" || d.siteNames[parts[1]]) ? parts[1] : "all";
  const openOnly = parts[2] !== "all";
  wrListHref = `#/work-requests/${site}${openOnly ? "" : "/all"}`;
  page(workRequestList(d.rows, d.siteNames, site, openOnly, flashHere), keepScroll);
}

/** Sent To CMMS, Close or Not Needed. The server checks the rule; the list reads again after. */
async function submitWorkRequestScreen(f: HTMLFormElement, act: string): Promise<void> {
  const requestId = f.dataset.id ?? "";
  const note = formVal(f, "note");
  wrScreen = { busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ requestId: string; status: string }>("screenWorkRequest", {
      requestId,
      action: act,
      workOrderNumber: formVal(f, "workOrderNumber"),
      workOrderSystem: formVal(f, "workOrderSystem"),
      priority: formVal(f, "priority"),
      resolution: act === "close" ? note : "",
      reason: act === "notNeeded" ? note : "",
    });
    await refreshWorkRequests();
    wrScreen = { busy: false, error: "" };
    const line = act === "sentToCmms" ? `${res.requestId} is In Work.` : act === "close" ? `${res.requestId} is closed.` : `${res.requestId} is closed as Not Needed.`;
    setFlash(line, "green", routeKey());
  } catch (e) {
    wrScreen = { busy: false, error: messageOf(e) };
  }
  if (route()[0] === "work-requests") render(true);
}

// ---- Near Miss (OBSERVATIONS_SCAN_WORDS.md rev 3 section 1) ----

async function refreshNearMisses(): Promise<void> {
  if (!badge) return;
  nmLoading = true;
  try {
    nmData = await loadNearMisses(badge.company);
  } catch (e) {
    nmData = `Could not read the near misses: ${messageOf(e)}`;
  } finally {
    nmLoading = false;
  }
}

/** #/near-miss/{site}, #/near-miss/{site}/all, #/near-miss/r/{observationId}. */
function renderNearMiss(keepScroll: boolean): void {
  if (typeof nmData === "string") {
    page(`${errorScreen(nmData)}<button class="prim" type="button" data-act="nm-retry">Try Again</button>`);
    return;
  }
  if (!nmData) {
    if (!nmLoading) void refreshNearMisses().then(() => route()[0] === "near-miss" && render(keepScroll));
    page(loadingScreen("the near misses"));
    return;
  }
  const d = nmData;
  const parts = route();
  const key = routeKey();
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  if (parts[1] === "r" && parts[2]) {
    const id = decodeURIComponent(parts[2]);
    const r = d.rows.find((x) => x.observationId === id);
    if (!r) {
      page(`<a class="back" href="${nmListHref}">Near Miss</a>${errorScreen("That near miss is not on record.")}`);
      return;
    }
    page(nearMissRecord(r, d.siteNames[r.siteNumber] ?? `Site ${r.siteNumber}`, nmListHref, nmScreen, flashHere), keepScroll);
    return;
  }
  const site = parts[1] && (parts[1] === "all" || d.siteNames[parts[1]]) ? parts[1] : "all";
  const openOnly = parts[2] !== "all";
  nmListHref = `#/near-miss/${site}${openOnly ? "" : "/all"}`;
  page(nearMissList(d.rows, d.siteNames, site, openOnly, flashHere), keepScroll);
}

/** Mark Reviewed or Make A Work Request. */
async function submitNearMissScreen(f: HTMLFormElement, act: string): Promise<void> {
  const observationId = f.dataset.id ?? "";
  nmScreen = { busy: true, error: "" };
  render(true);
  try {
    const res = await callServer<{ observationId: string; workRequestId: string }>("screenNearMiss", { observationId, action: act, note: formVal(f, "note") });
    await refreshNearMisses();
    if (res.workRequestId) wrData = null;
    nmScreen = { busy: false, error: "" };
    setFlash(res.workRequestId ? `${res.observationId} is reviewed. Work request ${res.workRequestId} is open.` : `${res.observationId} is reviewed.`, "green", routeKey());
  } catch (e) {
    nmScreen = { busy: false, error: messageOf(e) };
  }
  if (route()[0] === "near-miss") render(true);
}

// ---- Contractor Visits (OBSERVATIONS_SCAN_WORDS.md rev 3 section 2) ----

async function refreshVisits(): Promise<void> {
  if (!badge) return;
  cvLoading = true;
  try {
    cvData = await loadContractorVisits(badge.company);
  } catch (e) {
    cvData = `Could not read the contractor visits: ${messageOf(e)}`;
  } finally {
    cvLoading = false;
  }
}

/** #/contractors/{site}, #/contractors/r/{visitId}. */
function renderVisits(keepScroll: boolean): void {
  if (typeof cvData === "string") {
    page(`${errorScreen(cvData)}<button class="prim" type="button" data-act="cv-retry">Try Again</button>`);
    return;
  }
  if (!cvData) {
    if (!cvLoading) void refreshVisits().then(() => route()[0] === "contractors" && render(keepScroll));
    page(loadingScreen("the contractor visits"));
    return;
  }
  const d = cvData;
  const parts = route();
  const key = routeKey();
  const flashHere = flash && flash.route === key ? flashNote(flash) : "";
  const nowIso = new Date().toISOString();
  if (parts[1] === "r" && parts[2]) {
    const id = decodeURIComponent(parts[2]);
    const v = d.rows.find((x) => x.visitId === id);
    if (!v) {
      page(`<a class="back" href="${cvListHref}">Contractor Visits</a>${errorScreen("That visit is not on record.")}`);
      return;
    }
    page(visitRecord(v, d.siteNames[v.siteNumber] ?? `Site ${v.siteNumber}`, cvListHref, today(), nowIso, cvScreen, flashHere), keepScroll);
    return;
  }
  const site = parts[1] && (parts[1] === "all" || d.siteNames[parts[1]]) ? parts[1] : "all";
  cvListHref = `#/contractors/${site}`;
  page(visitList(d.rows, d.siteNames, site, cvSearch, today(), nowIso, flashHere), keepScroll);
}

async function markVisitSeen(visitId: string): Promise<void> {
  cvScreen = { busy: true, error: "" };
  render(true);
  try {
    await callServer("markVisitSeen", { visitId });
    await refreshVisits();
    cvScreen = { busy: false, error: "" };
    setFlash(`${visitId}: the Yes answers are marked seen.`, "green", routeKey());
  } catch (e) {
    cvScreen = { busy: false, error: messageOf(e) };
  }
  if (route()[0] === "contractors") render(true);
}

// ---- Sites asset page: the QR scan count (OBSERVATIONS_SCAN_WORDS.md rev 3 section 3) ----

// ---- One asset page (ONE_ASSET_PAGE_WORDS.md, approved 2026-10-24) ----

type AssetPageParts = Pick<AssetPageData, "doc" | "changes" | "iea" | "links" | "afLabel" | "study">;
const assetPages = new Map<string, AssetPageParts | string>();
const assetPageLoading = new Set<string>();

/** The asset's page parts, read once per visit. Null while they load. */
function needAssetPage(site: string, assetId: string): AssetPageParts | string | null {
  const k = `${site}/${assetId}`;
  const got = assetPages.get(k);
  if (got !== undefined) return got;
  if (!badge || assetPageLoading.has(k)) return null;
  assetPageLoading.add(k);
  const b = badge;
  void loadAssetPage(b.company, site, assetId, b.authorities.includes("ieaWalk"))
    .then((p) => assetPages.set(k, p ?? `${assetId} is not on record at this site.`))
    .catch((e) => assetPages.set(k, `Could not read the asset: ${messageOf(e)}`))
    .finally(() => {
      assetPageLoading.delete(k);
      const r = route();
      if (r[0] === "sites" && r[1] === site && r[2] === "assets" && decodeURIComponent(r[3] ?? "") === assetId) render(true);
    });
  return null;
}

function scanStateOf(assetId: string): ScanState {
  let s = scanStates.get(assetId);
  if (!s) {
    s = { count: null, confirming: false, busy: false, error: "" };
    scanStates.set(assetId, s);
    void callServer<{ count: number; from: ScanCountFrom | null }>("loadScanCount", { assetId })
      .then((r) => {
        s!.count = { count: r.count, line: scanCountLine(r.from) };
      })
      .catch((e) => {
        s!.count = `Could not count: ${messageOf(e)}`;
      })
      .then(() => {
        if (route()[0] === "sites" && route()[2] === "assets" && decodeURIComponent(route()[3] ?? "") === assetId) render(true);
      });
  }
  return s;
}

async function resetScans(assetId: string): Promise<void> {
  const s = scanStates.get(assetId);
  if (!s || s.busy) return;
  s.busy = true;
  s.error = "";
  render(true);
  try {
    const r = await callServer<{ count: number; from: ScanCountFrom }>("resetScanCount", { assetId, confirmed: true });
    s.count = { count: r.count, line: scanCountLine(r.from) };
    s.confirming = false;
    setFlash(`The QR scan count for ${assetId} starts again from 0.`, "green", routeKey());
  } catch (e) {
    s.error = messageOf(e);
  }
  s.busy = false;
  render(true);
}

// ---- Gloves roster (GLOVES_WORDS.md, GLOVES_REWORK_WORDS.md) ----

async function refreshGloves(): Promise<void> {
  if (!badge) return;
  glovesLoading = true;
  try {
    glovesData = await loadGlovesData(badge.company);
  } catch (e) {
    glovesData = `Could not read the gloves: ${messageOf(e)}`;
  } finally {
    glovesLoading = false;
  }
}

function needGloves(keepScroll: boolean): GlovesData | null {
  if (typeof glovesData === "object" && glovesData) return glovesData;
  if (typeof glovesData === "string") {
    page(`${errorScreen(glovesData)}<button class="prim" type="button" data-act="gloves-retry">Try Again</button>`);
    return null;
  }
  if (!glovesLoading) {
    void refreshGloves().then(() => {
      if (route()[0] === "gloves") render(keepScroll);
    });
  }
  page(loadingScreen("gloves"));
  return null;
}

/** Every Active QEW badge, with the person's name and size. The admin badge is left out. */
function rosterFor(d: GlovesData): RosterRow[] {
  return rosterForData({ ...d, nodesDone: nodesOneToThreeDoneSet(d.records) }, today());
}

function renderGloves(keepScroll: boolean): void {
  const d = needGloves(keepScroll);
  if (!d) return;
  const parts = route();
  const rows = rosterFor(d);
  if (parts[1] === "p" && parts[2]) {
    const qewId = decodeURIComponent(parts[2]);
    const r = rows.find((x) => x.qewId === qewId);
    if (!r) {
      page(`<a class="back" href="#/gloves">Gloves</a>${errorScreen(`${qewId} is not an Active QEW.`)}`);
      return;
    }
    const p = d.people.find((x) => x.qewId === qewId);
    page(gloveRecord(r, personGloves(qewId, d.gloves, d.inspections), d.siteNames[r.siteNumber] ?? `Site ${r.siteNumber}`, p?.gloveSize ?? null), keepScroll);
    return;
  }
  const site = parts[1] && (parts[1] === "all" || d.siteNames[parts[1]]) ? parts[1] : "all";
  if (parts[2] === "enter" && site !== "all") {
    const flashHere = flash && flash.route === routeKey() ? flashNote(flash) : "";
    page(flashHere + glovesEnter(enterView(d, rows, site)), keepScroll);
    return;
  }
  const bucket = (["current", "soon", "expired", "none"] as const).find((b) => b === parts[2]) ?? null;
  page(glovesRoster(rows, d.siteNames, site, bucket), keepScroll);
}

// ---- Enter Gloves (GLOVES_ENTER_WORDS.md, MOCKUP_GLOVES_ENTER.html, approved 2026-10-24) ----

interface GloveEntry {
  site: string;
  cells: Record<string, EnterCell>;
  errors: Record<string, string>;
  saved: Record<string, string>;
  busy: boolean;
  hit: string;
}
let gloveEntry: GloveEntry | null = null;

function entryFor(site: string): GloveEntry {
  if (!gloveEntry || gloveEntry.site !== site) gloveEntry = { site, cells: {}, errors: {}, saved: {}, busy: false, hit: "" };
  return gloveEntry;
}

function enterRowsFor(d: GlovesData, rows: RosterRow[], site: string): EnterRow[] {
  const sizes = new Map(d.people.map((p) => [p.qewId, (p as { gloveSize?: string | null }).gloveSize ?? null]));
  return enterRows(
    rows
      .filter((r) => r.siteNumber === site)
      .map((r) => ({ qewId: r.qewId, name: r.name, locked: r.locked, gloveSize: sizes.get(r.qewId) ?? null, pair: personGloves(r.qewId, d.gloves, d.inspections).pair })),
    today(),
  );
}

function enterView(d: GlovesData, rows: RosterRow[], site: string): Parameters<typeof glovesEnter>[0] {
  const e = entryFor(site);
  const list = enterRowsFor(d, rows, site);
  // A cell starts with the size the person already has.
  for (const r of list) if (r.state === "open" && !e.cells[r.qewId]) e.cells[r.qewId] = { size: r.size, testedOn: "" };
  return { site, siteName: d.siteNames[site] ?? `Site ${site}`, siteNames: d.siteNames, rows: list, cells: e.cells, errors: e.errors, saved: e.saved, busy: e.busy, hit: e.hit };
}

/** Keeps the Save count and the yellow cells in step without drawing the sheet again. */
function syncEnterSave(): void {
  const e = gloveEntry;
  const btn = root.querySelector<HTMLButtonElement>('[data-act="ge-save"]');
  if (!e || !btn) return;
  const n = Object.values(e.cells).filter((c) => isFilled(c)).length;
  btn.textContent = saveLabel(n);
  btn.disabled = !n || e.busy;
}

/** Two taps on a row number, close together, copy its test date down (the Asset ID Creator's way). */
let geTap: { key: string; at: number } | null = null;
function enterRowTap(qewId: string): void {
  const now = Date.now();
  if (!geTap || geTap.key !== qewId || now - geTap.at > 400) {
    geTap = { key: qewId, at: now };
    return;
  }
  geTap = null;
  const d = typeof glovesData === "object" ? glovesData : null;
  const e = gloveEntry;
  if (!d || !e || e.busy) return;
  const list = enterRowsFor(d, rosterFor(d), e.site).filter((r) => !e.saved[r.qewId]);
  const res = copyDateDown(list, e.cells, qewId);
  if (!res.copied) return;
  e.cells = res.cells;
  e.hit = qewId;
  render(true);
  window.setTimeout(() => {
    if (gloveEntry?.hit === qewId) {
      gloveEntry.hit = "";
      root.querySelector(`[data-ge-rn="${CSS.escape(qewId)}"]`)?.classList.remove("hit");
    }
  }, 900);
}

async function saveEnteredGloves(): Promise<void> {
  const d = typeof glovesData === "object" ? glovesData : null;
  const e = gloveEntry;
  if (!d || !e || e.busy) return;
  const list = enterRowsFor(d, rosterFor(d), e.site).filter((r) => !e.saved[r.qewId]);
  const send = rowsToSend(list, e.cells);
  if (!send.length) return;
  // The page checks first, with the server's words, so a bad date never leaves the iPad.
  e.errors = {};
  const t = today();
  for (const x of send) {
    const why = enterProblem({ size: x.gloveSize, testedOn: x.testedOn }, t);
    if (why) e.errors[x.qewId] = why;
  }
  const good = send.filter((x) => !e.errors[x.qewId]);
  if (!good.length) {
    render(true);
    return;
  }
  e.busy = true;
  render(true);
  try {
    const res = await callServer<{ results: { qewId: string; pairId?: string; error?: string }[] }>("recordGlovesForSite", { siteNumber: e.site, rows: good });
    let ok = 0;
    for (const r of res.results) {
      if (r.error) e.errors[r.qewId] = r.error;
      else {
        const x = good.find((g) => g.qewId === r.qewId)!;
        e.saved[r.qewId] = `${(r.pairId ?? "").split("-").pop()} · sealed · size ${x.gloveSize} · tested ${gloveDate(x.testedOn)} · Recorded by ${badge?.holderName ?? "you"}`;
        delete e.cells[r.qewId];
        ok++;
      }
    }
    const bad = res.results.length - ok;
    setFlash(`${ok} ${ok === 1 ? "pair" : "pairs"} saved, sealed.${bad ? ` ${bad} did not save. The reason is on the row.` : " Each QEW sees theirs on the bag tag."}`, bad ? "red" : undefined);
    await refreshGloves();
  } catch (err) {
    setFlash(`Could not save: ${messageOf(err)}`, "red");
  } finally {
    if (gloveEntry) gloveEntry.busy = false;
    render(true);
  }
}

// ---- Session buttons ----

function siteNow(): string {
  return route()[1] ?? "01";
}

async function startSession(btn: HTMLButtonElement): Promise<void> {
  const site = siteNow();
  btn.disabled = true;
  btn.textContent = "Starting…";
  try {
    const res = await callServer<{ sessionId: string; sessionCode: string; endedOthers: string[] }>("startSession", { siteNumber: site });
    setFlash(
      `Session ${res.sessionId} started. Code ${res.sessionCode}.${res.endedOthers.length ? ` Ended ${res.endedOthers.join(", ")} first.` : ""}`,
    );
  } catch (e) {
    setFlash(messageOf(e), "red");
  }
  await refreshSessions(site);
  render(true);
}

async function openEndSheet(btn: HTMLButtonElement): Promise<void> {
  const site = siteNow();
  const s = sessions.get(site);
  if (!badge || !s || typeof s === "string" || !s.open) return;
  btn.disabled = true;
  try {
    const failed = await failedInSession(badge.company, s.open.sessionId);
    setSheet({ kind: "end", site, failed, ticked: false, busy: false, error: "" });
  } catch (e) {
    setFlash(`Could not read the session's attempts: ${messageOf(e)}`, "red");
  }
  render(true);
}

async function endSession(): Promise<void> {
  if (sheet?.kind !== "end") return;
  const sh = sheet;
  const s = sessions.get(sh.site);
  if (!s || typeof s === "string" || !s.open) return;
  const open = s.open;
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const res = await callServer<{ sessionId: string; alreadyEnded: boolean }>("endSession", {
      siteNumber: sh.site,
      sessionId: open.sessionId,
      reviewConfirmed: sh.failed.length > 0 && sh.ticked,
    });
    setSheet(null);
    setFlash(res.alreadyEnded ? `Session ${res.sessionId} had already ended.` : `Session ${res.sessionId} ended. Code ${open.sessionCode} no longer works.`);
    await refreshSessions(sh.site);
  } catch (e) {
    // A fail that landed after the sheet opened: the server sends the new list.
    const details = e instanceof ServerError ? (e.details as { failed?: FailedInSession[] } | undefined) : undefined;
    if (details?.failed) {
      sh.failed = details.failed;
      sh.ticked = false;
    }
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

async function reopenSession(): Promise<void> {
  if (sheet?.kind !== "reopen") return;
  const sh = sheet;
  sh.reason = (root.querySelector<HTMLSelectElement>("#reopen-reason")?.value ?? "").trim();
  sh.context = (root.querySelector<HTMLInputElement>("#reopen-context")?.value ?? "").trim();
  if (!sh.reason) {
    sh.error = "Pick a reason.";
    render(true);
    return;
  }
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const res = await callServer<{ sessionId: string; sessionCode: string; alreadyOpen: boolean; endedOthers?: string[] }>("reopenSession", {
      siteNumber: sh.site,
      sessionId: sh.sessionId,
      reason: sh.reason,
      context: sh.context,
    });
    setSheet(null);
    setFlash(
      res.alreadyOpen
        ? `Session ${res.sessionId} is already open.`
        : `Session ${res.sessionId} reopened. Code ${res.sessionCode} works again.${res.endedOthers?.length ? ` Ended ${res.endedOthers.join(", ")} first.` : ""}`,
    );
    await refreshSessions(sh.site);
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

// ---- Close by Hand and Edit Completion Date ----

async function submitCloseByHand(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const field = (n: string) => ((f.elements.namedItem(n) as HTMLInputElement | null)?.value ?? "").trim();
  const entry: CloseByHandEntry = { completedOn: field("completedOn"), correct: field("correct"), total: field("total"), evidence: field("evidence") };
  const qewId = f.dataset.qew ?? "";
  const nodeId = f.dataset.node as NodeId;
  form = { route: key, entry, date: null, busy: true, error: "" };
  render(true);
  try {
    await callServer("closeByHand", {
      qewId,
      nodeId,
      completedOn: entry.completedOn,
      evidence: entry.evidence,
      ...(entry.total ? { correct: Number(entry.correct), total: Number(entry.total) } : {}),
    });
    await refreshData();
    form = null;
    const back = `training/${siteNow()}/${qewId}`;
    setFlash(`Node ${nodeId.replace("node", "")} closed by hand.`, "green", back);
    goUpTo(back);
  } catch (e) {
    form = { route: key, entry, date: null, busy: false, error: messageOf(e) };
    render(true);
  }
}

async function submitEditDate(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const date = ((f.elements.namedItem("completedOn") as HTMLInputElement | null)?.value ?? "").trim();
  const recordId = f.dataset.record ?? "";
  form = { route: key, entry: null, date, busy: true, error: "" };
  render(true);
  try {
    await callServer("editCompletionDate", { recordId, completedOn: date });
    await refreshData();
    form = null;
    const back = route().slice(0, 3).join("/");
    setFlash("Completion date changed. Expiry follows the node.", "green", back);
    goUpTo(back);
  } catch (e) {
    form = { route: key, entry: null, date, busy: false, error: messageOf(e) };
    render(true);
  }
}

// ---- Quiz Bank buttons ----

function activeLine(r: { activeCount: number; target: number; warning: string | null }): string {
  return r.warning ? `${r.activeCount} active, target ${r.target}.` : `${r.activeCount} active, on target.`;
}

async function switchQuestion(): Promise<void> {
  if (sheet?.kind !== "switch" || !data) return;
  const sh = sheet;
  const q = data.questions.find((x) => x.questionId === sh.questionId);
  if (!q) return;
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const res = await callServer<{ isActive: boolean; activeCount: number; target: number; warning: string | null }>("setQuestionActive", {
      questionId: q.questionId,
      isActive: !q.isActive,
    });
    await refreshData();
    setSheet(null);
    setFlash(`Question ${q.questionNumber} switched ${res.isActive ? "on" : "off"}. ${activeLine(res)}`, res.warning ? "red" : "green");
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

async function savePassMark(): Promise<void> {
  if (sheet?.kind !== "passmark") return;
  const sh = sheet;
  sh.value = (root.querySelector<HTMLInputElement>("#pm-input")?.value ?? sh.value).trim();
  sh.busy = true;
  sh.error = "";
  render(true);
  try {
    const res = await callServer<{ quizId: string; from: number; to: number }>("setPassMark", {
      quizId: sh.quizId,
      passMarkPct: Number(sh.value),
      confirmed: true,
    });
    await refreshData();
    setSheet(null);
    setFlash(`Pass mark changed from ${res.from} to ${res.to} percent. The next attempt uses it.`);
  } catch (e) {
    sh.busy = false;
    sh.error = messageOf(e);
  }
  render(true);
}

async function saveQuestionForm(f: HTMLFormElement): Promise<void> {
  const key = routeKey();
  const val = (n: string) => ((f.elements.namedItem(n) as HTMLInputElement | null)?.value ?? "").trim();
  const checked = (n: string) => f.querySelector<HTMLInputElement>(`input[name="${n}"]:checked`)?.value ?? "";
  const type = checked("qtype") === "TF" ? "TF" : "MC";
  const entry: QuestionInput & { type: "MC" | "TF" } = {
    type,
    questionText: val("questionText"),
    optionA: val("optionA"),
    optionB: val("optionB"),
    optionC: type === "TF" ? "" : val("optionC"),
    optionD: type === "TF" ? "" : val("optionD"),
    correctAnswer: checked("correctAnswer"),
    slideRef: val("slideRef"),
  };
  const quizId = f.dataset.quiz ?? "";
  const questionId = f.dataset.q ?? "";
  // Same checks as the server, so a missing field is caught before the round trip.
  const problem = questionProblem(entry);
  if (problem) {
    qform = { route: key, entry, busy: false, error: problem };
    render(true);
    return;
  }
  qform = { route: key, entry, busy: true, error: "" };
  render(true);
  try {
    const { type: _t, ...fields } = entry;
    const res = await callServer<{ questionId: string; activeCount: number; target: number; warning: string | null }>("saveQuestion", {
      quizId,
      ...(questionId ? { questionId } : {}),
      ...fields,
    });
    await refreshData();
    qform = null;
    const back = `quiz-bank/${quizId}/${res.questionId}`;
    setFlash(`${questionId ? "Saved." : `Added as ${res.questionId}.`} ${activeLine(res)}`, res.warning && !questionId ? "red" : "green", back);
    goUpTo(back);
  } catch (e) {
    qform = { route: key, entry, busy: false, error: messageOf(e) };
    render(true);
  }
}

// ---- Events ----

function run(task: () => Promise<void>): void {
  if (acting) return;
  acting = true;
  void task().finally(() => {
    acting = false;
  });
}

// Drag and drop on the Lists page: the handle lifts a row among its own group; Done saves (B3).
dragOrder(root, {
  handle: ".ls-handle",
  scroller: () => document.scrollingElement as HTMLElement | null,
  onDrop: (id, from, to) => {
    const o = listOrder;
    if (!o || o.route !== routeKey() || from === to) return render(true);
    const ids = Object.values(o.view.ids).find((list) => list.includes(id));
    if (!ids) return render(true);
    ids.splice(to, 0, ...ids.splice(from, 1));
    o.view.moves += 1;
    o.view.error = "";
    render(true);
  },
});

/** Order on a list: its order now, by group. Classes are one group, "". */
function startListOrder(): void {
  const d = typeof listsData === "object" ? listsData : null;
  const parts = route();
  if (!d || parts[0] !== "lists") return;
  let ids: Record<string, string[]> = {};
  let kind: "class" | "value" = "class";
  let listName = "";
  if (parts[1] === "classes") ids = { "": sortClasses(d.classes).map((c) => c.code) };
  else if (parts[1] === "fields" && parts[2]) {
    listName = listNameFromSlug(d, parts[2]) ?? "";
    if (!listName) return;
    kind = "value";
    for (const g of groupValues(d.values, listName)) ids[g.group] = g.values.map((v) => v.id);
  } else return;
  listOrder = { route: routeKey(), kind, listName, view: { ids, moves: 0, error: "", busy: false }, start: JSON.parse(JSON.stringify(ids)) as Record<string, string[]> };
  render(true);
}

/** Done: each group that changed goes to the server in one call. A failure keeps Order open, nothing lost. */
async function saveListOrder(): Promise<void> {
  const o = listOrder;
  if (!o) return;
  const changed = Object.keys(o.view.ids).filter((g) => o.view.ids[g]!.join() !== (o.start[g] ?? []).join());
  if (!changed.length) {
    listOrder = null;
    render(true);
    return;
  }
  o.view.busy = true;
  o.view.error = "";
  render(true);
  try {
    for (const group of changed) await callServer("moveListItem", { kind: o.kind, ids: o.view.ids[group], listName: o.listName, group });
    await refreshLists();
    listOrder = null;
    setFlash("Order saved. The IEA Site Survey uses it the next time it loads.");
  } catch (e) {
    o.view.busy = false;
    o.view.error = isOnline() ? messageOf(e) : "No signal. Nothing was saved. Tap Done again when you have signal.";
  }
  render(true);
}

root.addEventListener("click", (ev) => {
  if (quizLink || guestLink) return;
  const target = ev.target as HTMLElement;
  if (target.classList.contains("shade") && !(sheet && sheet.busy)) {
    setSheet(null);
    render(true);
    return;
  }
  if (route()[0] === "settings" && route()[1] === "drive") {
    if (badge && onDriveSettingsClick(target, badge.company, () => render(true))) return;
  }
  if (route()[0] === "settings" && route()[1] === "printer" && onPrinterSetupClick(target, () => render(true))) return;
  if (badge && onQewPrintClick(target, { company: badge.company, badgeId: badge.badgeId, holderName: badge.holderName, repaint: () => render(true), badges: () => (typeof peopleData === "object" && peopleData ? peopleData.badges : []) })) return;
  const rn = target.closest<HTMLElement>("[data-ge-rn]");
  if (rn) {
    enterRowTap(rn.dataset.geRn ?? "");
    return;
  }
  const el = target.closest<HTMLElement>("[data-act]");
  if (!el || (el as HTMLButtonElement).disabled) return;
  switch (el.dataset.act) {
    case "ge-save":
      void saveEnteredGloves();
      return;
    case "qew-print": {
      const site = route()[2] ?? "";
      if (/^\d{2}$/.test(site)) {
        openQewPrint(site);
        render(true);
      }
      return;
    }
    case "asset-page-retry":
      assetPages.clear();
      render(true);
      return;
    case "training-retry":
      void loadTrainingNow();
      render();
      return;
    case "signout":
      // Robert, 2026-10-28: the next sign-in always opens Home, not the page he signed out from.
      // replaceState changes the address without a redraw; the sign-out redraw then shows the sign-in.
      history.replaceState(null, "", `${location.pathname}${location.search}#/`);
      void signOutNow();
      return;
    case "start-session":
      run(() => startSession(el as HTMLButtonElement));
      return;
    case "end-session":
      run(() => openEndSheet(el as HTMLButtonElement));
      return;
    case "end-confirm":
      run(endSession);
      return;
    case "reopen":
      if (el.dataset.id) {
        setSheet({ kind: "reopen", site: siteNow(), sessionId: el.dataset.id, busy: false, error: "", reason: "", context: "" });
        render(true);
      }
      return;
    case "reopen-confirm":
      run(reopenSession);
      return;
    case "qb-switch": {
      const qid = route()[2];
      if (qid) {
        setSheet({ kind: "switch", questionId: qid, busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "qb-switch-confirm":
      run(switchQuestion);
      return;
    case "pm-open": {
      const quizId = quizIdFrom(route()[1]);
      const cur = data ? summarizeQuizzes(data.quizzes, data.questions).find((x) => x.quiz.quizId === quizId)?.quiz.passMarkPct : undefined;
      setSheet({ kind: "passmark", quizId, value: String(cur ?? 80), busy: false, error: "" });
      render(true);
      return;
    }
    case "pm-confirm":
      run(savePassMark);
      return;
    case "site-active": {
      const id = siteNumberNow();
      if (id) {
        setSheet({ kind: "siteActive", site: id, busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "site-active-confirm":
      run(confirmSiteActive);
      return;
    case "site-zone": {
      const id = siteNumberNow();
      const s = typeof sitesData === "object" ? sitesData?.sites.find((x) => x.siteNumber === id) : undefined;
      if (s) {
        setSheet({ kind: "siteZone", site: id, picked: siteZone(s), busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "site-zone-pick":
      if (sheet?.kind === "siteZone" && !sheet.busy) {
        sheet.picked = el.dataset.zone ?? sheet.picked;
        render(true);
      }
      return;
    case "site-zone-save":
      run(saveSiteZone);
      return;
    case "auth": {
      const qewId = qewNow();
      const k = el.dataset.key as Authority | undefined;
      const b = typeof peopleData === "object" ? peopleData?.badges.find((x) => x["Badge ID"] === qewId) : undefined;
      // Never a dead tap. With no badge record loaded the switch reads Off, so the confirm turns it on
      // and the server says plainly if the badge is missing.
      if (qewId && k) {
        setSheet({ kind: "auth", qewId, key: k, isOn: !(b?.Authorities ?? []).includes(k), busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "auth-confirm":
      run(confirmAuthority);
      return;
    case "person-active": {
      const qewId = qewNow();
      if (qewId) {
        setSheet({ kind: "personActive", qewId, busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "person-active-confirm":
      run(confirmPersonActive);
      return;
    case "move":
      run(() => moveItem(el));
      return;
    case "list-order":
      startListOrder();
      return;
    case "list-order-cancel":
      listOrder = null;
      render(true);
      return;
    case "list-order-done":
      run(saveListOrder);
      return;
    case "item-active": {
      const d = typeof listsData === "object" ? listsData : null;
      const itemKind = el.dataset.kind === "class" ? "class" : "value";
      const id = el.dataset.id ?? "";
      const item = itemKind === "class" ? d?.classes.find((x) => x.code === id) : d?.values.find((x) => x.id === id);
      if (item) {
        const name = "name" in item ? item.name : item.value;
        setSheet({ kind: "itemActive", itemKind, id, name, isOn: !item.isActive, busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "item-active-confirm":
      run(confirmItemActive);
      return;
    case "class-save-confirm":
      run(confirmClassSave);
      return;
    case "plant-delete": {
      const n = route()[2];
      if (n) {
        setSheet({ kind: "plantDelete", siteNumber: n, busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "plant-delete-confirm":
      run(confirmPlantDelete);
      return;
    case "gloves-retry":
      glovesData = null;
      render();
      return;
    case "lists-retry":
      listsData = null;
      render();
      return;
    case "tasks-retry":
      tasksData = null;
      render();
      return;
    case "refs-retry":
      refsData = null;
      render();
      return;
    case "em-retry":
      emailData = null;
      render();
      return;
    case "wr-retry":
      wrData = null;
      render();
      return;
    case "nm-retry":
      nmData = null;
      render();
      return;
    case "cv-retry":
      cvData = null;
      render();
      return;
    case "cv-seen":
      run(() => markVisitSeen(el.dataset.id ?? ""));
      return;
    case "scan-reset":
    case "scan-reset-no": {
      const s = scanStates.get(decodeURIComponent(route()[3] ?? ""));
      if (s && !s.busy) {
        s.confirming = el.dataset.act === "scan-reset";
        s.error = "";
        render(true);
      }
      return;
    }
    case "scan-reset-go":
      run(() => resetScans(decodeURIComponent(route()[3] ?? "")));
      return;
    case "em-test":
      run(sendTestEmails);
      return;
    case "ref-open":
      void openRefFile(el.dataset.path ?? "");
      return;
    case "ref-retire": {
      const id = route()[2] ? decodeURIComponent(route()[2]!) : "";
      if (id) {
        setSheet({ kind: "refRetire", refId: id, reason: "", busy: false, error: "" });
        render(true);
        root.querySelector<HTMLInputElement>("#ref-reason")?.focus();
      }
      return;
    }
    case "ref-retire-confirm":
      run(confirmRefRetire);
      return;
    case "ref-edition-confirm":
      run(confirmRefEdition);
      return;
    case "task-switch": {
      const d = typeof tasksData === "object" ? tasksData : null;
      const t = d?.tasks.find((x) => x.taskId === el.dataset.id);
      if (t) {
        setSheet({ kind: "taskSwitch", taskId: t.taskId, isOn: !t.isActive, reason: "", busy: false, error: "" });
        render(true);
        root.querySelector<HTMLInputElement>("#task-reason")?.focus();
      }
      return;
    }
    case "task-switch-confirm":
      run(confirmTaskSwitch);
      return;
    case "task-change": {
      const id = route()[2] ? decodeURIComponent(route()[2]!) : "";
      const field = el.dataset.field === "tier" ? "tier" : "taskType";
      if (id) {
        setSheet({ kind: "taskChange", taskId: id, field, to: "", reason: "", busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "task-change-confirm":
      run(confirmTaskChange);
      return;
    case "task-sop-retry": {
      const d = typeof tasksData === "object" ? tasksData : null;
      const t = d?.tasks.find((x) => x.taskId === taskSop?.taskId);
      const sop = d?.sops.find((s) => s.sopNumber === t?.sopNumber);
      if (t && sop) {
        dropTaskSop();
        render(true);
      }
      return;
    }
    case "setting-switch": {
      const field = el.dataset.field as SwitchField | undefined;
      const d = typeof settingsData === "object" ? settingsData : null;
      if (field && d) {
        setSheet({ kind: "settingSwitch", field, isOn: !d.settings[field], busy: false, error: "" });
        render(true);
      }
      return;
    }
    case "setting-switch-confirm":
      run(confirmSettingSwitch);
      return;
    case "limit-confirm":
      run(confirmLimit);
      return;
    case "settings-retry":
      settingsData = null;
      render();
      return;
    case "people-retry":
      peopleData = null;
      render();
      return;
    case "sites-retry":
      sitesData = null;
      render();
      return;
    case "hub-retry":
      hubProgram.delete(siteNumberNow());
      render(true);
      return;
    case "home-guest": {
      const first = homeSites().find((x) => x.siteNumber === "01") ?? homeSites()[0];
      setSheet({ kind: "guest", site: first?.siteNumber ?? "01", key: "msd", busy: false });
      render(true);
      return;
    }
    case "home-guest-open":
      // The link opens the new tab itself. Home stays, with the sheet closed.
      setSheet(null);
      setTimeout(() => render(true), 0);
      return;
    case "sop-review-retry":
      dropSopReview();
      render(true);
      return;
    case "home-refresh":
      void refreshHomeStages();
      return;
    case "hub-mark":
      void markStep(el.dataset.step ?? "");
      return;
    case "add-site":
      if (!isOnline()) return;
      setSheet({ kind: "addSite", entry: { siteNumber: nextAddSiteNumber(), name: "", city: "", state: "", timeZone: "" }, busy: false, error: "", step: "" });
      render(true);
      return;
    case "add-site-plant": {
      if (sheet?.kind !== "addSite" || sheet.busy) return;
      const n = el.dataset.plant ?? "";
      const p = typeof listsData === "object" && listsData ? listsData.plants.find((x) => x.siteNumber === n) : undefined;
      if (!p) return;
      // Tap the chip again to let it go: back to the next number up, fields cleared (change 4).
      sheet.entry = sheet.entry.fromPlant && sheet.entry.siteNumber === p.siteNumber
        ? { siteNumber: nextAddSiteNumber(), name: "", city: "", state: "", timeZone: "" }
        : { siteNumber: p.siteNumber, name: p.name, city: p.city, state: p.state, fromPlant: true, timeZone: zoneFromState(p.state) };
      sheet.error = "";
      render(true);
      return;
    }
    case "add-site-confirm":
      void confirmAddSite();
      return;
    case "sheet-cancel":
      if (!sheet?.busy) {
        setSheet(null);
        render(true);
      }
      return;
    default:
      return;
  }
});

root.addEventListener("change", (ev) => {
  if (quizLink || guestLink) return;
  const el = ev.target as HTMLInputElement;
  if (route()[0] === "settings" && route()[1] === "printer" && onPrinterSetupChange(el, () => render(true))) return;
  if ((el.name === "gp-site" || el.name === "gp-label") && sheet?.kind === "guest") {
    if (el.name === "gp-site") sheet.site = el.value;
    else sheet.key = el.value as DemoLabelKey;
    const id = root.querySelector<HTMLElement>("#gp-id");
    const open = root.querySelector<HTMLAnchorElement>("#gp-open");
    if (id) id.textContent = demoLabelId(sheet.site, sheet.key);
    if (open) open.href = guestUrl(sheet.site, sheet.key);
    return;
  }
  if (el.id === "ref-file" && refAdd) {
    refAdd.file = el.files?.[0] ?? null;
    refAdd.error = "";
    if (refAdd.file && !refAdd.entry.title.trim() && el.form?.dataset.mode === "new") refAdd.entry.title = refAdd.file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ");
    render(true);
    return;
  }
  if (el.form?.id === "ref-form") {
    syncRefForm(el.form);
    return;
  }
  if (el.name === "task-to" && sheet?.kind === "taskChange") {
    sheet.to = el.value;
    sheet.error = "";
    syncTaskSheetButton();
    return;
  }
  if (el.form?.id === "task-form") {
    syncTaskForm(el.form);
    return;
  }
  if (el.name === "role" && el.form?.id === "contact-form") {
    const f = el.form;
    const ticks = usedForForRole(el.value, readContactForm(f).usedFor);
    for (const u of USED_FOR) {
      const box = f.elements.namedItem(u.key) as HTMLInputElement | null;
      if (box) box.checked = Boolean(ticks[u.key]);
    }
    return;
  }
  if (el.name === "siteNumber" && el.form?.id === "add-person-form") {
    addEdit = { route: routeKey(), entry: readAddForm(el.form), busy: false, error: "" };
    render(true);
    return;
  }
  if (el.dataset.act === "nm-site") {
    replacingHash = true;
    location.replace(`#/near-miss/${el.value}${route()[2] === "all" ? "/all" : ""}`);
    return;
  }
  if (el.dataset.act === "cv-site") {
    replacingHash = true;
    location.replace(`#/contractors/${el.value}`);
    return;
  }
  if (el.dataset.geSize !== undefined || el.dataset.geDate !== undefined) {
    const e = gloveEntry;
    const id = el.dataset.geSize ?? el.dataset.geDate ?? "";
    if (!e) return;
    const c = e.cells[id] ?? { size: "", testedOn: "" };
    e.cells[id] = el.dataset.geSize !== undefined ? { ...c, size: el.value } : { ...c, testedOn: el.value };
    delete e.errors[id];
    el.classList.toggle("need", !el.value);
    syncEnterSave();
    return;
  }
  if (el.dataset.act === "ge-site") {
    // Another site is the same sheet for that site, not a new step.
    replacingHash = true;
    location.replace(`#/gloves/${el.value}/enter`);
    return;
  }
  if (el.dataset.act === "gl-site") {
    // Another site in the picker is the same page, not a new step. A count filter stays on.
    replacingHash = true;
    location.replace(`#/gloves/${el.value}${route()[2] && route()[2] !== "p" ? `/${route()[2]}` : ""}`);
    return;
  }
  if (el.dataset.act === "wr-site") {
    // Another site in the picker is the same page, not a new step. The All filter stays on.
    replacingHash = true;
    location.replace(`#/work-requests/${el.value}${route()[2] === "all" ? "/all" : ""}`);
    return;
  }
  if (el.dataset.act === "em-site") {
    // Another site in the picker is the same page, not a new step. The Failed filter stays on.
    replacingHash = true;
    location.replace(`#/email-log/${el.value}${route()[2] === "failed" ? "/failed" : ""}`);
    return;
  }
  if (el.dataset.act === "site") {
    // Another site in the picker is the same page, not a new step.
    replacingHash = true;
    location.replace(`#/training/${el.value}`);
    return;
  }
  if (el.name === "qtype") {
    const tf = el.value === "TF";
    root.querySelectorAll<HTMLElement>(".qb-cd").forEach((x) => (x.hidden = tf));
    const form = el.form;
    if (tf && form) {
      const a = form.elements.namedItem("optionA") as HTMLInputElement | null;
      const b = form.elements.namedItem("optionB") as HTMLInputElement | null;
      if (a && !a.value.trim()) a.value = "True";
      if (b && !b.value.trim()) b.value = "False";
      form.querySelectorAll<HTMLInputElement>('input[name="correctAnswer"][value="C"], input[name="correctAnswer"][value="D"]').forEach((r) => (r.checked = false));
    }
    return;
  }
  if (el.id === "end-review" && sheet?.kind === "end") {
    sheet.ticked = el.checked;
    const btn = root.querySelector<HTMLButtonElement>('[data-act="end-confirm"]');
    if (btn) btn.disabled = !el.checked || sheet.busy;
  }
});

root.addEventListener("input", (ev) => {
  if (quizLink || guestLink) return;
  if ((ev.target as HTMLElement).tagName === "INPUT" && onQewPrintInput(ev.target as HTMLInputElement, () => render(true), root)) return;
  const el = ev.target as HTMLInputElement;
  if (el.form?.id === "add-site-form" && sheet?.kind === "addSite") {
    const k = el.name;
    if (k === "name" || k === "city" || k === "state") sheet.entry[k] = el.value;
    // Time Zone (SITE_TIME_ZONE_WORDS.md): fills from a one-zone state until Robert picks one himself.
    if (k === "timeZone") {
      sheet.entry.timeZone = el.value;
      sheet.entry.zonePicked = true;
    }
    if (k === "state" && !sheet.entry.zonePicked) sheet.entry.timeZone = zoneFromState(el.value);
    if (k === "state" || k === "timeZone") {
      const zone = sheet.entry.timeZone ?? "";
      const hint = addSiteZoneHint(sheet.entry.state, zone);
      const sel = root.querySelector<HTMLSelectElement>("#as-tz");
      const from = root.querySelector("#as-tz-from");
      const note = root.querySelector<HTMLElement>("#as-tz-note");
      const go = root.querySelector<HTMLButtonElement>("#as-go");
      if (sel) {
        sel.value = zone;
        sel.classList.toggle("amber", !zone);
      }
      if (from) from.textContent = hint.from;
      if (note) {
        note.textContent = hint.note;
        note.hidden = !hint.note;
      }
      if (go) go.disabled = !zone;
    }
    const lines = addSiteLines(sheet.entry.siteNumber, sheet.entry.name);
    const f = root.querySelector("#as-folder");
    const t = root.querySelector("#as-tags");
    if (f) f.textContent = lines.folder;
    if (t) t.textContent = lines.tags;
    return;
  }
  if (el.id === "home-site-search") {
    homeSearch = el.value;
    drawHomeSites();
    return;
  }
  if (el.id === "ref-search") {
    refSearch = el.value;
    drawRefGroups();
    return;
  }
  if (el.form?.id === "ref-form") {
    syncRefForm(el.form);
    return;
  }
  if (el.id === "ref-reason" && sheet?.kind === "refRetire") {
    sheet.reason = el.value;
    const btn = root.querySelector<HTMLButtonElement>('[data-act="ref-retire-confirm"]');
    if (btn && !sheet.busy) btn.disabled = !el.value.trim();
    return;
  }
  if (el.id === "task-reason" && (sheet?.kind === "taskSwitch" || sheet?.kind === "taskChange")) {
    sheet.reason = el.value;
    syncTaskSheetButton();
    return;
  }
  if (el.id === "task-search") {
    taskSearch = el.value;
    drawTaskGroups();
    return;
  }
  if (el.form?.id === "task-form") {
    syncTaskForm(el.form);
    return;
  }
  if (el.name === "value" && el.form?.id === "limit-form") {
    const field = el.form.dataset.field as LimitField;
    const def = LIMITS.find((l) => l.field === field);
    const out = root.querySelector<HTMLElement>("#limit-check");
    if (def && out) {
      const problem = limitProblem(field, el.value);
      out.textContent = problem ?? `Between ${def.min.toLocaleString("en-US")} and ${def.max.toLocaleString("en-US")}.`;
      out.classList.toggle("bad", Boolean(problem));
    }
    return;
  }
  if (el.id === "people-search") {
    peopleSearch = el.value;
    drawPeopleGroups();
    return;
  }
  if (el.id === "cv-search") {
    // Typing keeps its place: only the rows draw again.
    cvSearch = el.value;
    const host = root.querySelector<HTMLElement>("#cv-rows");
    if (host && typeof cvData === "object" && cvData) {
      const tmp = document.createElement("div");
      const site = route()[1] && (route()[1] === "all" || cvData.siteNames[route()[1]!]) ? route()[1]! : "all";
      tmp.innerHTML = visitList(cvData.rows, cvData.siteNames, site, cvSearch, today(), new Date().toISOString());
      host.innerHTML = tmp.querySelector("#cv-rows")?.innerHTML ?? "";
    }
    return;
  }
  if (el.id === "asset-search") {
    const site = siteNumberNow();
    assetSearch.set(site, el.value);
    drawAssetGroups(site);
    return;
  }
  if (el.id === "pm-input" && sheet?.kind === "passmark" && data) {
    const sh = sheet;
    sh.value = el.value;
    const qs = summarizeQuizzes(data.quizzes, data.questions).find((x) => x.quiz.quizId === sh.quizId);
    const stats = root.querySelector("#pm-stats");
    if (qs && stats) stats.innerHTML = passMarkStats(qs, el.value);
    const n = Number(el.value);
    const btn = root.querySelector<HTMLButtonElement>('[data-act="pm-confirm"]');
    if (btn && qs) btn.disabled = !(Number.isInteger(n) && n >= 1 && n <= 100) || n === qs.quiz.passMarkPct;
    return;
  }
  if (el.name !== "completedOn") return;
  const parts = route();
  const t = data ? rosterForSite(data.records, parts[1] ?? "01", data.qewTags, data.attempts, today(), data.stations, data.loto).find((x) => x.qewId === parts[2]) : undefined;
  if (!t) return;
  if (parts[3] === "close") {
    const out = root.querySelector("#cbh-exp");
    if (out) out.textContent = expiryPreview(el.value, parts[4] as NodeId);
  } else if (parts[3] === "date") {
    const r = t.nodes.find((n) => n.record?.recordId === parts[4])?.record;
    const out = root.querySelector("#edd-exp");
    if (out && r) out.textContent = expiryPreview(el.value, r.nodeId);
  }
});

root.addEventListener("submit", (ev) => {
  if (quizLink || guestLink) return;
  // Survey forms are handled by the survey page (survey-page.ts).
  if ((ev.target as HTMLElement).closest("[data-sv-form]")) return;
  ev.preventDefault();
  const f = ev.target as HTMLFormElement;
  if (f.id === "cbh-form") {
    run(() => submitCloseByHand(f));
    return;
  }
  if (f.id === "qb-form") {
    run(() => saveQuestionForm(f));
    return;
  }
  if (f.id === "edd-form") {
    run(() => submitEditDate(f));
    return;
  }
  if (f.id === "site-form") {
    run(() => submitSiteForm(f));
    return;
  }
  if (f.id === "contact-form") {
    run(() => submitContactForm(f));
    return;
  }
  if (f.id === "class-form") {
    submitClassForm(f);
    return;
  }
  if (f.id === "value-form") {
    run(() => submitValueForm(f));
    return;
  }
  if (f.id === "plant-form") {
    run(() => submitPlantForm(f));
    return;
  }
  if (f.id === "task-form") {
    run(() => submitTaskForm(f));
    return;
  }
  if (f.id === "ref-form") {
    run(() => submitRefForm(f));
    return;
  }
  if (f.id === "ref-edit-form") {
    run(() => submitRefEdit(f));
    return;
  }
  if (f.id === "limit-form") {
    submitLimitForm(f);
    return;
  }
  if (f.id === "program-form") {
    run(() => submitProgramForm(f));
    return;
  }
  if (f.id === "program-contact-form") {
    run(() => submitProgramContactForm(f));
    return;
  }
  if (f.id === "person-form") {
    run(() => submitPersonForm(f));
    return;
  }
  if (f.id === "add-person-form") {
    run(() => submitAddForm(f));
    return;
  }
  if (f.id === "out-form") {
    run(() => submitOutForm(f));
    return;
  }
  if (f.id === "verify-form") {
    run(() => submitVerifyForm(f));
    return;
  }
  if (f.id === "nm-form") {
    const act = ((ev as SubmitEvent).submitter as HTMLButtonElement | null)?.value ?? "";
    run(() => submitNearMissScreen(f, act));
    return;
  }
  if (f.id === "wr-form") {
    const act = ((ev as SubmitEvent).submitter as HTMLButtonElement | null)?.value ?? "";
    run(() => submitWorkRequestScreen(f, act));
    return;
  }
  const email = (f.elements.namedItem("email") as HTMLInputElement | null)?.value ?? "";
  const password = (f.elements.namedItem("password") as HTMLInputElement | null)?.value ?? "";
  const btn = f.querySelector("button")!;
  btn.disabled = true;
  btn.textContent = "Signing in…";
  signIn(email, password).catch(() => {
    root.innerHTML = signInScreen("Email or password did not match. Try again.", adminReturnPath());
  });
});

document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape" && sheet && !sheet.busy) {
    setSheet(null);
    render(true);
  }
});

window.addEventListener("hashchange", () => {
  // A page change by address: an open sheet closes, and its Back step no longer applies.
  sheet = null;
  steps().reset();
  trackHash();
  // A message belongs to the page it was written for (NAV_CRAWL_WORDS.md A2). Leave that page and it is gone,
  // so "Details saved." does not greet him again the next time he opens it.
  if (flash && flash.route !== routeKey()) flash = null;
  if (form && form.route !== routeKey()) form = null;
  if (qform && qform.route !== routeKey()) qform = null;
  if (siteForm && siteForm.route !== routeKey()) siteForm = null;
  if (contactEdit && contactEdit.route !== routeKey()) contactEdit = null;
  if (personEdit && personEdit.route !== routeKey()) personEdit = null;
  if (addEdit && addEdit.route !== routeKey()) addEdit = null;
  if (outEdit && outEdit.route !== routeKey()) outEdit = null;
  if (verifyEdit && verifyEdit.route !== routeKey()) verifyEdit = null;
  if (route()[0] !== "email-log" && !emailTest.busy) emailTest = { busy: false, error: "", note: "" };
  // A refusal on one request never follows to another page.
  if (!routeKey().startsWith("work-requests/r/") && !wrScreen.busy) wrScreen = { busy: false, error: "" };
  if (!routeKey().startsWith("near-miss/r/") && !nmScreen.busy) nmScreen = { busy: false, error: "" };
  if (!routeKey().startsWith("contractors/r/") && !cvScreen.busy) cvScreen = { busy: false, error: "" };
  for (const s of scanStates.values()) if (!s.busy) s.confirming = false;
  if (programEdit && programEdit.route !== routeKey()) programEdit = null;
  if (pcEdit && pcEdit.route !== routeKey()) pcEdit = null;
  if (limitEdit && limitEdit.route !== routeKey()) limitEdit = null;
  if (classEdit && classEdit.route !== routeKey()) classEdit = null;
  if (valueEdit && valueEdit.route !== routeKey()) valueEdit = null;
  if (plantEdit && plantEdit.route !== routeKey()) plantEdit = null;
  if (taskAdd && taskAdd.route !== routeKey()) taskAdd = null;
  if (refAdd && refAdd.route !== routeKey()) refAdd = null;
  if (refEdit && refEdit.route !== routeKey()) refEdit = null;
  if (sopReview && !(route()[0] === "sop-library" && decodeURIComponent(route()[1] ?? "") === sopReview.sopNumber)) dropSopReview();
  if (taskSop && !(route()[0] === "tasks" && route()[3] === "sop" && decodeURIComponent(route()[2] ?? "") === taskSop.taskId)) dropTaskSop();
  render();
});

async function boot(): Promise<void> {
  registerServiceWorker();
  root.innerHTML = loadingScreen("SafeTag");
  try {
    await startFirebase();
  } catch (e) {
    if (!isOnline()) holdForSignal(root, "SafeTag");
    // Something to tap, never words alone (NAV_AUDIT.md item 7). Try Again reloads this same address.
    else {
      root.innerHTML = `${errorScreen(messageOf(e))}<button class="prim" type="button" id="boot-retry">Try Again</button>`;
      root.querySelector("#boot-retry")?.addEventListener("click", () => location.reload());
    }
    return;
  }
  // No signal: only the IEA Site Survey keeps working. Every other page waits for signal.
  const surveyHere = !decisionLink && !sponsorLink && !quizLink && !guestLink && opensOffline(location.hash);
  if (!isOnline() && !surveyHere) {
    const admin = !guestLink && !quizLink && !decisionLink && !sponsorLink;
    holdForSignal(root, admin ? "Admin pages" : "This page", admin);
    return;
  }
  if (decisionLink) {
    mountDecidePage(root, decisionLink);
    return;
  }
  if (sponsorLink) {
    mountSponsorPage(root, sponsorLink);
    return;
  }
  // Opened from Admin Home to preview: a way back, since the Home Screen app has no browser Back.
  if ((quizLink || guestLink) && cameFromAdmin(location.search)) mountAdminWayBack(document);
  if (quizLink) {
    useSlot("field");
    mountQuizPage(root, quizLink);
    return;
  }
  if (guestLink) {
    // Guest, tag and QEW pages work in the field sign-in slot. An admin signed in elsewhere is untouched.
    useSlot("field");
    if (guestLink.kind === "asset") {
      const { assetId, company } = guestLink;
      // A refresh on a QEW page with the visit still open goes back to that page, not the guest page.
      if (pageFromHash(location.hash) && (await openQewVisit())) mountFieldAccess(root, company, { kind: "asset", assetId, siteName: "" });
      else mountGuestPage(root, assetId, company, (siteName) => mountFieldAccess(root, company, { kind: "asset", assetId, siteName }));
    } else if (guestLink.kind === "badge") {
      const { company } = guestLink;
      mountTagPage(root, guestLink.badgeId, company, (view) => mountFieldAccess(root, company, { kind: "tag", view }));
    }
    else if (guestLink.kind === "refused") {
      // A label link the app can't read: offer the camera for the next label (NAV_AUDIT.md item 7).
      root.innerHTML = `${errorScreen(guestLink.message)}<button class="prim" type="button" id="boot-scan">Scan A Label</button>`;
      root.querySelector("#boot-scan")?.addEventListener("click", () => openScanAgain(root, ""));
    }
    return;
  }
  // Once a QEW Dashboard opens here, Field Access owns the screen, sign-out included.
  let qewOwns = false;
  watchSignIn(async (user) => {
    if (qewOwns) return;
    badge = null;
    data = null;
    dataError = "";
    sessions.clear();
    sheet = null;
    steps().reset();
    sitesData = null;
    siteAssets.clear();
    peopleData = null;
    settingsData = null;
    listsData = null;
    glovesData = null;
    tasksData = null;
    refsData = null;
    emailData = null;
    emailTest = { busy: false, error: "", note: "" };
    wrData = null;
    wrScreen = { busy: false, error: "" };
    nmData = null;
    cvData = null;
    scanStates.clear();
    dropTaskSop();
    if (!user) {
      // No admin here. A QEW visit still open in this browser opens his Dashboard, unless he tapped
      // through to the admin sign-in. The Dashboard checks the 1-hour idle.
      const qew = takeAdminRequest() ? null : await openQewVisit();
      if (qew) {
        qewOwns = true;
        useSlot("field");
        mountFieldAccess(root, qew.company, { kind: "dashboard" });
        return;
      }
      root.innerHTML = signInScreen("", adminReturnPath());
      return;
    }
    const b = await badgeOf(user);
    if (isQewSignIn(b)) {
      // A QEW sign-in left in the admin slot from before the slots were split (2026-10-10). Clear it.
      // The watch runs again signed out.
      await signOutNow("admin").catch(() => undefined);
      return;
    }
    if (!b || !b.authorities.includes("admin")) {
      root.innerHTML = noBadgeScreen(user.email ?? "This login");
      return;
    }
    badge = b;
    clearAdminReturn();
    render();
    // No signal: the survey runs from the device. Training data loads once signal is back.
    if (!isOnline()) {
      window.addEventListener("online", () => location.reload(), { once: true });
      return;
    }
    await loadTrainingNow();
  });
}

void boot();
